import PdfPreview from "../components/ui/PdfPreview"
import type { User } from "../slices/authSlice"
import CurrencySelector from "../components/layout/CurrencySelector"
import { ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { SpinnerText } from "../components/ui/Spinner"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { RootState } from "../store/store"
import AppLayout from "../layout/AppLayout"
import PatentFormStepper, {
  PatentFormStep
} from "../components/patent/PatentFormStepper"
import ReferenceNumberSection from "../components/trademark/reference_number/ReferenceNumberSection"
import PatentApplicantAndRepresentative from "../components/patent/PatentApplicantAndRepresentative"
import StateSelectionPanel from "../components/patent/StateSelectionPanel"
import FileUploadSlot from "../components/patent/FileUploadSlot"
import PatentEpPhaseFeesSection from "../components/patent/PatentEpPhaseFeesSection"
import SignatureSection from "../components/trademark/signature/SignatureSection"
import StatusToast from "../components/ui/StatusToast"
import StatusBadge from "../components/ui/StatusBadge"
import PatentApplicationFeesBox from "../components/patent/PatentApplicationFeesBox"
import ServiceDescriptions from "../components/ui/ServiceDescriptions"
import { FilingSidebarAccordion } from "../components/ui/FilingSidebarAccordion"
import {
  getPatentApplicationById,
  updatePatentApplicationStatus,
  validatePatentApplication
} from "../services/patentApplicationService"
import {
  uploadPatentDocument,
  PatentDocumentType
} from "../services/patentDocumentService"
import { savePatentDraft } from "../services/savePatentDraftService"
import { lookupPctApplication } from "../services/epoOpsService"
import { addRetrievedApplicants, isApplicantComplete } from "../utils/pctApplicants"
import { authorityOption, describeFilled, languageOption } from "../utils/pctLookupFields"
import { formatApiError } from "../utils/formatApiError"
import {
  SEQUENCE_LISTING_FILE_TYPES,
  validatePatentPdfFile,
  validatePatentSequenceListingFile,
  validatePatentZipFile
} from "../utils/fileUploadUtils"
import { resetForm } from "../hooks/resetForm"
import { FiChevronDown, FiChevronUp, FiCreditCard, FiRefreshCw, FiSave, FiSearch } from "react-icons/fi"
import { useDraftStep } from "../hooks/useDraftStep"
import { PATENT_PROCEDURE_LABELS } from "../utils/patentProcedure"
import {
  epoAdmissibleNonOfficialLanguages,
  pctPublicationLanguages,
  EPO_EXTENSION_STATES,
  EPO_VALIDATION_STATES
} from "../utils/countryData"

type EpPhaseValue = {
  entryRole: "DESIGNATED_OFFICE" | "ELECTED_OFFICE"
  internationalPublicationLanguage: string
  proceedingsLanguage: string
  internationalFilingDate: string
  pctApplicationNumber: string
  internationalPublicationNumber: string
  epApplicationNumber: string
  internationalSearchingAuthority: string
  requestExaminationInNonOfficialLanguage: boolean
  admissibleNonEpoLanguage: string
  acceleratedProcessing: boolean
  waiveInvitationToMaintainApplication: boolean
  waiveRule161Communication: boolean
  feeReductionSmallEntity: boolean
  feeReductionNaturalPersonOrEntity: boolean
  extensionStatesRequested: boolean
  extensionStateMode: "all" | "selected"
  selectedExtensionStates: string[]
  validationStatesRequested: boolean
  validationStateMode: "all" | "selected"
  selectedValidationStates: string[]
  biologicalMaterial: boolean
  biologicalMaterialDeposits: BiologicalMaterialDeposit[]
  sequenceListing: boolean
  sequenceListingOption: "" | "available" | "attached"
  sequenceListingStandard: "" | "ST25" | "ST26"
  sequenceListingDocument: EpPhaseUploadDocument | null
  priorityTranslationDocument: EpPhaseUploadDocument | null
  internationalApplicationTranslationDocument: EpPhaseUploadDocument | null
  iperAnnexTranslationDocument: EpPhaseUploadDocument | null
  amendedClaimsTranslationDocument: EpPhaseUploadDocument | null
  amendedDescriptionDocument: EpPhaseUploadDocument | null
  amendedClaimsDocument: EpPhaseUploadDocument | null
  amendedDrawingsDocument: EpPhaseUploadDocument | null
  unconvertedArchiveDocument: EpPhaseUploadDocument | null
  amendedReplacementPagesAttached: boolean
  isaObservationsAttached: boolean
  priorityApplicationTranslationComplete: boolean
  sequenceListingContentConfirmed: boolean
  descriptionPageCount: string
  claimsForEuropeanPhaseCount: string
  claimsPageCount: string
  claimsFeePaidCount: string
  drawingPageCount: string
  descriptionPctPublished: boolean
  descriptionTranslation: boolean
  descriptionArticle34Amendment: boolean
  descriptionArticle34Translation: boolean
  descriptionEpPhaseAmendments: boolean
  claimsPctPublished: boolean
  claimsArticle19Amendment: boolean
  claimsArticle34Amendment: boolean
  claimsEpPhaseAmendments: boolean
  drawingsPctPublished: boolean
  drawingsTranslation: boolean
  drawingsArticle34Amendment: boolean
  drawingsEpPhaseAmendments: boolean
  descriptionPctPublishedPageRange: string
  descriptionPctPublishedPageCount: string
  descriptionTranslationPageRange: string
  descriptionTranslationPageCount: string
  descriptionArticle34AmendmentPageRange: string
  descriptionArticle34AmendmentPageCount: string
  descriptionArticle34TranslationPageRange: string
  descriptionArticle34TranslationPageCount: string
  descriptionEpPhaseAmendmentsPageRange: string
  descriptionEpPhaseAmendmentsPageCount: string
  claimsPctPublishedPageRange: string
  claimsPctPublishedPageCount: string
  claimsArticle19AmendmentPageRange: string
  claimsArticle19AmendmentPageCount: string
  claimsArticle34AmendmentPageRange: string
  claimsArticle34AmendmentPageCount: string
  claimsEpPhaseAmendmentsPageRange: string
  claimsEpPhaseAmendmentsPageCount: string
  drawingsPctPublishedPageRange: string
  drawingsPctPublishedPageCount: string
  drawingsTranslationPageRange: string
  drawingsTranslationPageCount: string
  drawingsArticle34AmendmentPageRange: string
  drawingsArticle34AmendmentPageCount: string
  drawingsEpPhaseAmendmentsPageRange: string
  drawingsEpPhaseAmendmentsPageCount: string
  additionalDocumentsEnabled: boolean
  additionalDocuments: AdditionalDocument[]

}

type EpPhaseUploadDocument = {
  type: PatentDocumentType
  fileName: string
  epoDocumentId?: string
}

type AdditionalDocument = {
  id: string
  documentKind: string
  customName: string
  document: EpPhaseUploadDocument | null
}

type AmendmentTranslationUploadType =
  | "iperAnnexTranslation"
  | "amendedClaimsTranslation"
  | "amendedDescription"
  | "amendedClaims"
  | "amendedDrawings"
  | "priorityTranslation"
  | "internationalTranslation"
  | "unconvertedArchive"

type BiologicalMaterialDeposit = {
  id: string
  reference: string
  depositaryInstitution: string
  accessionNumber: string
  depositInformationInApplication: boolean
  pageNumber: string
  lineNumber: string
  receiptCertificateStatus: "attached" | "filedLater"
  receiptCertificateUploadRequested: boolean
  receiptCertificateDocument: EpPhaseUploadDocument | null
  rule33WaiverAttached: boolean
  rule33WaiverUploadRequested: boolean
  rule33WaiverDocument: EpPhaseUploadDocument | null
}

const EMPTY_EP_PHASE: EpPhaseValue = {
  entryRole: "DESIGNATED_OFFICE",
  internationalPublicationLanguage: "",
  proceedingsLanguage: "English",
  internationalFilingDate: "",
  pctApplicationNumber: "PCT",
  internationalPublicationNumber: "WO",
  epApplicationNumber: "EP",
  internationalSearchingAuthority: "",
  requestExaminationInNonOfficialLanguage: false,
  admissibleNonEpoLanguage: "",
  acceleratedProcessing: false,
  waiveInvitationToMaintainApplication: false,
  waiveRule161Communication: false,
  feeReductionSmallEntity: false,
  feeReductionNaturalPersonOrEntity: false,
  extensionStatesRequested: false,
  extensionStateMode: "all",
  selectedExtensionStates: [],
  validationStatesRequested: false,
  validationStateMode: "all",
  selectedValidationStates: [],
  biologicalMaterial: false,
  biologicalMaterialDeposits: [],
  sequenceListing: false,
  sequenceListingOption: "",
  sequenceListingStandard: "",
  sequenceListingDocument: null,
  priorityTranslationDocument: null,
  internationalApplicationTranslationDocument: null,
  iperAnnexTranslationDocument: null,
  amendedClaimsTranslationDocument: null,
  amendedDescriptionDocument: null,
  amendedClaimsDocument: null,
  amendedDrawingsDocument: null,
  unconvertedArchiveDocument: null,
  amendedReplacementPagesAttached: false,
  isaObservationsAttached: false,
  priorityApplicationTranslationComplete: false,
  sequenceListingContentConfirmed: false,
  descriptionPageCount: "",
  claimsForEuropeanPhaseCount: "",
  claimsPageCount: "",
  claimsFeePaidCount: "",
  drawingPageCount: "",
  descriptionPctPublished: false,
  descriptionTranslation: false,
  descriptionArticle34Amendment: false,
  descriptionArticle34Translation: false,
  descriptionEpPhaseAmendments: false,
  claimsPctPublished: false,
  claimsArticle19Amendment: false,
  claimsArticle34Amendment: false,
  claimsEpPhaseAmendments: false,
  drawingsPctPublished: false,
  drawingsTranslation: false,
  drawingsArticle34Amendment: false,
  drawingsEpPhaseAmendments: false,
  descriptionPctPublishedPageRange: "",
  descriptionPctPublishedPageCount: "",
  descriptionTranslationPageRange: "",
  descriptionTranslationPageCount: "",
  descriptionArticle34AmendmentPageRange: "",
  descriptionArticle34AmendmentPageCount: "",
  descriptionArticle34TranslationPageRange: "",
  descriptionArticle34TranslationPageCount: "",
  descriptionEpPhaseAmendmentsPageRange: "",
  descriptionEpPhaseAmendmentsPageCount: "",
  claimsPctPublishedPageRange: "",
  claimsPctPublishedPageCount: "",
  claimsArticle19AmendmentPageRange: "",
  claimsArticle19AmendmentPageCount: "",
  claimsArticle34AmendmentPageRange: "",
  claimsArticle34AmendmentPageCount: "",
  claimsEpPhaseAmendmentsPageRange: "",
  claimsEpPhaseAmendmentsPageCount: "",
  drawingsPctPublishedPageRange: "",
  drawingsPctPublishedPageCount: "",
  drawingsTranslationPageRange: "",
  drawingsTranslationPageCount: "",
  drawingsArticle34AmendmentPageRange: "",
  drawingsArticle34AmendmentPageCount: "",
  drawingsEpPhaseAmendmentsPageRange: "",
  drawingsEpPhaseAmendmentsPageCount: "",
  additionalDocumentsEnabled: false,
  additionalDocuments: []

}

const FEES_STEP_LABEL = "Fees"
const SIGNATURE_STEP_LABEL = "Signature"

const FORM_STEPS = [
  "EP phase entry",
  "Parties",
  "States, BIOM, SEQL",
  "Documents",
  FEES_STEP_LABEL,
  SIGNATURE_STEP_LABEL
]

const PROCEEDINGS_LANGUAGES = ["English", "French", "German"]
const EPO_OFFICIAL_LANGUAGES = PROCEEDINGS_LANGUAGES

// Translations that go with the amendments: of the annexes to the
// international preliminary examination report (publication language not an
// EPO language) and of amended claims (EPO as designated Office)
const TRANSLATION_UPLOADS = [
  {
    key: "iperAnnexTranslation",
    field: "iperAnnexTranslationDocument",
    documentType: "IPER_TRANSLATION",
    label: "Translation of the annexes to the international preliminary examination report"
  },
  {
    key: "amendedClaimsTranslation",
    field: "amendedClaimsTranslationDocument",
    documentType: "AMENDED_CLAIMS_TRANSLATION",
    label: "Translation of the amended claims"
  }
] as const

// Amendments on entry into the European phase: one PDF per part
const AMENDMENT_UPLOADS = [
  {
    key: "amendedDescription",
    field: "amendedDescriptionDocument",
    documentType: "AMENDED_DESCRIPTION",
    label: "Amended description"
  },
  {
    key: "amendedClaims",
    field: "amendedClaimsDocument",
    documentType: "AMENDED_CLAIMS",
    label: "Amended claims"
  },
  {
    key: "amendedDrawings",
    field: "amendedDrawingsDocument",
    documentType: "AMENDED_DRAWINGS",
    label: "Amended drawings"
  }
] as const
const ADDITIONAL_DOCUMENT_TYPES = [
  "General authorisation",
  "Specific authorisation",
  "Certified priority document",
  "Request for retrieval of the priority document via WIPO DAS",
  "Other documents",
  "Response to the written opinion/IPER (EPO as elected Office)",
  "Response to the written opinion/IPER (EPO as designated Office)",
  "Search results required under Rule 141(1) EPC",
  "Amended claims with annotations",
  "Amended description with annotations",
  "Amended drawings with annotations"
]

const DEPOSITARY_INSTITUTIONS = [
  "ABC - Advanced Biotechnology Center (Italy)",
  "NRRL - Agricultural Research Service Culture Collection (USA)",
  "VKM - All-Russian Collection of Microorganisms (Russian Federation)",
  "ATCC - American Type Culture Collection (USA)",
  "BEA - Banco Espanol de Algas (Spain)",
  "BCCM - Belgian Coordinated Collections of Microorganisms (Belgium)",
  "IMI - CABI Bioscience, UK Centre (United Kingdom)",
  "CCTCC - China Center for Type Culture Collection (China)",
  "CGMCC - China General Microbiological Culture Collection Center (China)",
  "CChRGM - Coleccion Chilena de Recursos Geneticos Microbianos (Chile)",
  "CM-CNRG - Coleccion de Microorganismos del Centro Nacional de Recursos Geneticos (Mexico)",
  "CECT - Coleccion Espanola de Cultivos Tipo (Spain)",
  "CNCM - Collection nationale de cultures de micro-organismes (France)",
  "CCAP - Culture Collection of Algae and Protozoa (United Kingdom)",
  "CCOS - Culture Collection of Switzerland (Switzerland)",
  "CCY - Culture Collection of Yeasts (Slovak Republic)",
  "CCM - Czech Collection of Microorganisms (Czech Republic)",
  "ECACC - European Collection of Cell Cultures (United Kingdom)",
  "GDMCC - Guangdong Microbial Culture Collection Center (China)",
  "IAFB - Collection of Industrial Microorganisms (Poland)",
  "DBVPG - Collection of Industrial Yeasts (Italy)",
  "IDAC - International Depositary Authority of Canada (Canada)",
  "IPOD - International Patent Organism Depositary (Japan)",
  "IZSLER - Istituto Zooprofilattico Sperimentale della Lombardia e dell'Emilia Romagna (Italy)",
  "KACC - Korean Agricultural Culture Collection (Republic of Korea)",
  "KCLRF - Korean Cell Line Research Foundation (Republic of Korea)",
  "KCTC - Korean Collection for Type Cultures (Republic of Korea)",
  "KCCM - Korean Culture Center of Microorganisms (Republic of Korea)",
  "CBA - Lady Mary Fairfax CellBank Australia (Australia)",
  "DSMZ - Leibniz-Institut DSMZ - Deutsche Sammlung von Mikroorganismen und Zellkulturen GmbH (Germany)",
  "MCC - Microbial Culture Collection (India)",
  "MSCL - Microbial Strain Collection of Latvia (Latvia)",
  "MTCG - Microbial Type Culture Collection and Gene Bank (India)",
  "NBIMCC - National Bank for Industrial Microorganisms and Cell Cultures (Bulgaria)",
  "NCAIM - National Collection of Agricultural and Industrial Microorganisms (Hungary)",
  "NCTC - National Collection of Type Cultures (United Kingdom)",
  "NCYC - National Collection of Yeast Cultures (United Kingdom)",
  "NCIMB - National Collections of Industrial, Food and Marine Bacteria (United Kingdom)",
  "NIBSC - National Institute for Biological Standards and Control (United Kingdom)",
  "NPMD - National Institute of Technology and Evaluation, Patent NMI - Microorganisms Depositary (Japan)",
  "NMI - National Measurement Institute (Australia)",
  "PCM - Polish Collection of Microorganisms (Poland)",
  "NCMA - Provasoli-Guillard National Center for Marine Algae and Microbiota (USA)",
  "VKPM - Russian National Collection of Industrial Microorganisms (Russian Federation)",
  "VTTCC - VTT Culture Collection (Finland)",
  "CBS - Westerdijk Fungal Biodiversity Institute (Netherlands)",
  "OTHER"
]
const SEARCH_AUTHORITIES = [
  "AT - Austrian Patent Office",
  "AU - IP Australia",
  "BR - National Institute of Industrial Property (Brazil)",
  "CA - Canadian Intellectual Property Office",
  "CL - National Institute of Industrial Property (Chile)",
  "CN - China National Intellectual Property Administration",
  "EA - Eurasian Patent Office",
  "EG - Egyptian Patent Office",
  "EP - European Patent Office",
  "ES - Spanish Patent and Trademark Office",
  "FI - Finnish Patent and Registration Office",
  "IL - Israel Patent Office",
  "IN - Indian Patent Office",
  "JP - Japan Patent Office",
  "KR - Korean Intellectual Property Office",
  "PH - Intellectual Property Office of the Philippines",
  "RU - Federal Service for Intellectual Property (Rospatent)",
  "SA - Saudi Authority for Intellectual Property",
  "SE - Swedish Intellectual Property Office",
  "SG - Intellectual Property Office of Singapore",
  "TR - Turkish Patent and Trademark Office",
  "UA - Ukrainian Intellectual Property Institute",
  "US - United States Patent and Trademark Office",
  "XN - Nordic Patent Institute",
  "XV - Visegrad Patent Institute"
]

function emptyBiologicalMaterialDeposit(): BiologicalMaterialDeposit {
  return {
    id: crypto.randomUUID(),
    reference: "",
    depositaryInstitution: "",
    accessionNumber: "",
    depositInformationInApplication: false,
    pageNumber: "",
    lineNumber: "",
    receiptCertificateStatus: "attached",
    receiptCertificateUploadRequested: false,
    receiptCertificateDocument: null,
    rule33WaiverAttached: false,
    rule33WaiverUploadRequested: false,
    rule33WaiverDocument: null
  }
}

function toDateInput(value: string | Date | null | undefined) {
  if (!value) return ""
  return String(value).slice(0, 10)
}

function todayInputValue() {
  const today = new Date()
  const offset = today.getTimezoneOffset()
  return new Date(today.getTime() - offset * 60 * 1000).toISOString().slice(0, 10)
}

function isFutureDate(value: string) {
  return Boolean(value) && value > todayInputValue()
}

function fieldClass(hasError: boolean, disabled?: boolean) {
  return `w-full max-w-sm rounded-md border px-3 py-2 text-sm text-dantzBlue focus:border-dantzOrange focus:outline-none focus:ring-1 focus:ring-dantzOrange disabled:cursor-not-allowed disabled:bg-gray-100 ${
    hasError ? "border-red-500 bg-white" : "border-gray-300 bg-white"
  } ${disabled ? "cursor-not-allowed" : ""}`
}

function SelectField({
  label,
  value,
  options,
  required,
  disabled,
  onChange
}: {
  label: string
  value: string
  options: string[]
  required?: boolean
  disabled?: boolean
  onChange: (value: string) => void
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-dantzBlue">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <select
        value={value}
        disabled={disabled}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
        className={fieldClass(false, disabled)}
      >
        <option value="">- Select -</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  )
}

function TextField({
  label,
  value,
  required,
  disabled,
  placeholder,
  max,
  type = "text",
  action,
  onChange
}: {
  label: string
  value: string
  required?: boolean
  disabled?: boolean
  placeholder?: string
  max?: string
  type?: "text" | "date"
  action?: ReactNode
  onChange: (value: string) => void
}) {
  const hasError = Boolean(type === "date" && isFutureDate(value))

  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-dantzBlue">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative w-full max-w-sm">
        <input
          type={type}
          value={value}
          max={max}
          disabled={disabled}
          autoComplete="off"
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className={`min-w-0 ${fieldClass(hasError, disabled)} ${action ? "pr-12" : ""}`}
        />
        {/* The button sits inside the field, so every field keeps the same width */}
        {action && <div className="absolute inset-y-1 right-1 flex">{action}</div>}
      </div>
      {type === "date" && isFutureDate(value) && (
        <p className="mt-1 text-xs text-red-500">
          Filing date cannot be in the future.
        </p>
      )}
    </div>
  )
}

function RadioRow({
  label,
  checked,
  disabled,
  name,
  onChange
}: {
  label: string
  checked: boolean
  disabled?: boolean
  name: string
  onChange: () => void
}) {
  return (
    <label className="flex items-center gap-3 text-sm text-dantzBlue">
      <input
        type="radio"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="h-4 w-4 border-gray-300 text-dantzBlue focus:ring-dantzBlue disabled:cursor-not-allowed disabled:opacity-50"
      />
      <span>{label}</span>
    </label>
  )
}

function CheckboxRow({
  label,
  checked,
  disabled,
  onChange,
  muted
}: {
  label: string
  checked: boolean
  disabled?: boolean
  muted?: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <label
      className={`flex items-start gap-3 text-sm ${
        disabled || muted ? "cursor-not-allowed" : "cursor-pointer"
      } ${muted ? "text-gray-400" : "text-dantzBlue"}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue disabled:cursor-not-allowed"
      />
      <span>{label}</span>
    </label>
  )
}

function pageInputClass(hasError: boolean, disabled?: boolean) {
  return `w-full max-w-sm rounded-md border px-3 py-2 text-sm text-dantzBlue focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-100 ${
    hasError
      ? "border-red-400 bg-white focus:border-red-500 focus:ring-1 focus:ring-red-200"
      : "border-gray-300 bg-white focus:border-dantzOrange focus:ring-1 focus:ring-dantzOrange"
  } ${disabled ? "cursor-not-allowed" : ""}`
}

function PageNumberInputSlot({
  visible,
  value,
  disabled,
  onChange,
  hasError
}: {
  visible: boolean
  value: string
  disabled?: boolean
  hasError?: boolean
  onChange: (value: string) => void
}) {
  if (!visible) return <div className="min-h-[2.5rem]" />

  return (
    <div className="min-h-[2.5rem]">
      <input
        type="number"
        min={1}
        step={1}
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(hasError)}
        onChange={(event) => onChange(event.target.value)}
        className={pageInputClass(Boolean(hasError), disabled)}
      />
      {hasError && (
        <div className="mt-1 text-xs text-red-600">
          Does not match the page range.
        </div>
      )}
    </div>
  )
}

function PageRangeInputSlot({
  visible,
  value,
  disabled,
  hasError,
  onChange
}: {
  visible: boolean
  value: string
  disabled?: boolean
  hasError?: boolean
  onChange: (value: string) => void
}) {
  if (!visible) return <div className="min-h-[2.5rem]" />

  return (
    <div className="min-h-[2.5rem]">
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder="e.g. 1-12"
        aria-invalid={Boolean(hasError)}
        onChange={(event) => onChange(event.target.value)}
        className={pageInputClass(Boolean(hasError), disabled)}
      />
      {hasError && (
        <div className="mt-1 text-xs text-red-600">
          Enter a valid range.
        </div>
      )}
    </div>
  )
}

function StateRequestPanel({
  type,
  mode,
  selectedStates,
  disabled,
  onModeChange,
  onStateToggle
}: {
  type: "extension" | "validation"
  mode: "all" | "selected"
  selectedStates: string[]
  disabled?: boolean
  onModeChange: (mode: "all" | "selected") => void
  onStateToggle: (code: string, checked: boolean) => void
}) {
  const isExtension = type === "extension"

  return (
    <StateSelectionPanel
      groupName={`ep1200-${type}-mode`}
      allLabel={isExtension ? "All extension states" : "All validation states"}
      selectedLabel="Selected states"
      options={isExtension ? EPO_EXTENSION_STATES : EPO_VALIDATION_STATES}
      selectedStates={selectedStates}
      selectionMode={mode}
      disabled={disabled}
      notice={
        isExtension ? (
          <span>
            Request for extension of the European patent granted on the present
            application to all non-contracting states designated in the PCT
            application for which an extension agreement with the EPO was in
            force on the international filing date. The request is deemed
            withdrawn if the extension fee is not paid in due time.
          </span>
        ) : (
          <span>
            Request for validation of the European patent granted on the present
            application in all non-contracting states designated in the PCT
            application for which a validation agreement with the EPO was in
            force on the international filing date. The request is deemed
            withdrawn if the validation fee is not paid in due time.
          </span>
        )
      }
      footnote={
        !isExtension && (mode === "all" || selectedStates.includes("KH")) ? (
          <span>
            KH Cambodia: pharmaceutical products are excluded from patent
            protection in Cambodia until 2033.
          </span>
        ) : undefined
      }
      onSelectionModeChange={onModeChange}
      onStateToggle={onStateToggle}
    />
  )
}


function EpPhaseAttachmentTable({
  label,
  document,
  uploadVisible,
  uploading,
  error,
  disabled,
  onAdd,
  onSelect,
  onDelete
}: {
  label: string
  document?: EpPhaseUploadDocument | null
  uploadVisible: boolean
  uploading: boolean
  error: string
  disabled?: boolean
  onAdd: () => void
  onSelect: (file: File) => void
  onDelete: () => void
}) {
  return (
    <div className="mt-4 bg-white">
      <div className="flex justify-end bg-gray-100 px-4 py-3">
        <button
          type="button"
          onClick={onAdd}
          disabled={disabled || uploadVisible || Boolean(document)}
          className="bg-dantzBlue px-6 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-dantzLightBlue disabled:opacity-60"
        >
          Add
        </button>
      </div>
      <div className="min-h-16 px-6 py-4">
        {(uploadVisible || document) && (
          <FileUploadSlot
            label={label}
            fileName={document?.fileName}
            documentId={document?.epoDocumentId}
            uploading={uploading}
            error={error}
            disabled={disabled}
            onSelect={onSelect}
            onDelete={onDelete}
          />
        )}
      </div>
    </div>
  )
}

export default function PatentEpPhaseFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isViewMode = location.pathname.includes("/view")
  const isAdmin = useSelector((state: RootState) => Boolean(state.auth.user?.isAdmin))
  const [referenceNumber, setReferenceNumber] = useState("")
  const [applicants, setApplicants] = useState<any[] | null>(null)
  const [epPhase, setEpPhase] = useState<EpPhaseValue>(EMPTY_EP_PHASE)
  const [signature, setSignature] = useState<any | null>(null)
  const [orderingParty, setOrderingParty] = useState<Partial<User> | null | undefined>(undefined)
  const [draftId, setDraftId] = useState<string | null>(null)
  const [feeRefreshToken, setFeeRefreshToken] = useState(0)
  const [applicationStatus, setApplicationStatus] = useState<"DRAFT" | "VALIDATED" | "AWAITING_RELEASE" | "PAYMENT_PENDING" | "ERROR" | "PAID">("DRAFT")
  const [validationError, setValidationError] = useState("")
  const [pctLookup, setPctLookup] = useState({ loading: false, message: "", failed: false })
  const [successMessage, setSuccessMessage] = useState("")
  const [uploadingEpPhaseDocument, setUploadingEpPhaseDocument] =
    useState<string | null>(null)
  const [epPhaseUploadErrors, setEpPhaseUploadErrors] = useState<Record<string, string>>({})
  const [selectedAdditionalDocumentIds, setSelectedAdditionalDocumentIds] = useState<string[]>([])
  const [additionalDocumentModalOpen, setAdditionalDocumentModalOpen] = useState(false)
  const [additionalDocumentKind, setAdditionalDocumentKind] = useState("")
  const [additionalDocumentName, setAdditionalDocumentName] = useState("")
  const [additionalDocumentFile, setAdditionalDocumentFile] =
    useState<EpPhaseUploadDocument | null>(null)
  const additionalDocumentUploadRequestRef = useRef(0)
  const [activeAmendmentUpload, setActiveAmendmentUpload] =
    useState<AmendmentTranslationUploadType | null>(null)
  const [amendmentMenuOpen, setAmendmentMenuOpen] = useState(false)
  const amendmentMenuRef = useRef<HTMLDivElement | null>(null)
  const [loading, setLoading] = useState(Boolean(id))

  const signatureId = signature?._id ?? null
  const isSubmitted =
    applicationStatus === "PAID" ||
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE"
  const isLocked = isViewMode || (!isAdmin && isSubmitted)
  const { step, setStep, savingDraft, goToStep: goToStepBase, runSaving } = useDraftStep(isLocked)
  const statusMustStayLocked =
    applicationStatus === "PAID" ||
    applicationStatus === "PAYMENT_PENDING" ||
    applicationStatus === "AWAITING_RELEASE" ||
    applicationStatus === "ERROR"
  const parsePageCount = (value: string) => {
    const trimmed = value.trim()
    return /^[1-9]\d*$/.test(trimmed) ? Number(trimmed) : 0
  }
  const getPageRangeCount = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return null

    const singlePage = trimmed.match(/^([1-9]\d*)$/)
    if (singlePage) return 1

    const pageRange = trimmed.match(/^([1-9]\d*)\s*(?:-|to|\s)\s*([1-9]\d*)$/i)
    if (!pageRange) return null

    const from = Number(pageRange[1])
    const to = Number(pageRange[2])

    return to >= from ? to - from + 1 : null
  }
  const pageRowValid = (selected: boolean, range: string, count: string) => {
    if (!selected) return true

    const parsedCount = parsePageCount(count)
    const rangeCount = getPageRangeCount(range)

    return parsedCount > 0 && rangeCount !== null && parsedCount === rangeCount
  }
  const setPageRangeAndCount = <
    RangeKey extends keyof EpPhaseValue,
    CountKey extends keyof EpPhaseValue
  >(rangeKey: RangeKey, countKey: CountKey, value: string) => {
    const rangeCount = getPageRangeCount(value)

    setEpPhase((current) => ({
      ...current,
      [rangeKey]: value,
      ...(rangeCount !== null ? { [countKey]: String(rangeCount) } : {})
    }))
  }
  const descriptionPctPublishedPagesValid = pageRowValid(
    epPhase.descriptionPctPublished,
    epPhase.descriptionPctPublishedPageRange,
    epPhase.descriptionPctPublishedPageCount
  )
  const descriptionTranslationPagesValid = pageRowValid(
    epPhase.descriptionTranslation,
    epPhase.descriptionTranslationPageRange,
    epPhase.descriptionTranslationPageCount
  )
  const descriptionArticle34AmendmentPagesValid = pageRowValid(
    epPhase.descriptionArticle34Amendment,
    epPhase.descriptionArticle34AmendmentPageRange,
    epPhase.descriptionArticle34AmendmentPageCount
  )
  const descriptionArticle34TranslationPagesValid = pageRowValid(
    epPhase.descriptionArticle34Translation,
    epPhase.descriptionArticle34TranslationPageRange,
    epPhase.descriptionArticle34TranslationPageCount
  )
  const descriptionEpPhaseAmendmentsPagesValid = pageRowValid(
    epPhase.descriptionEpPhaseAmendments,
    epPhase.descriptionEpPhaseAmendmentsPageRange,
    epPhase.descriptionEpPhaseAmendmentsPageCount
  )
  const claimsPctPublishedPagesValid = pageRowValid(
    epPhase.claimsPctPublished,
    epPhase.claimsPctPublishedPageRange,
    epPhase.claimsPctPublishedPageCount
  )
  const claimsArticle19AmendmentPagesValid = pageRowValid(
    epPhase.claimsArticle19Amendment,
    epPhase.claimsArticle19AmendmentPageRange,
    epPhase.claimsArticle19AmendmentPageCount
  )
  const claimsArticle34AmendmentPagesValid = pageRowValid(
    epPhase.claimsArticle34Amendment,
    epPhase.claimsArticle34AmendmentPageRange,
    epPhase.claimsArticle34AmendmentPageCount
  )
  const claimsEpPhaseAmendmentsPagesValid = pageRowValid(
    epPhase.claimsEpPhaseAmendments,
    epPhase.claimsEpPhaseAmendmentsPageRange,
    epPhase.claimsEpPhaseAmendmentsPageCount
  )
  const drawingsPctPublishedPagesValid = pageRowValid(
    epPhase.drawingsPctPublished,
    epPhase.drawingsPctPublishedPageRange,
    epPhase.drawingsPctPublishedPageCount
  )
  const drawingsTranslationPagesValid = pageRowValid(
    epPhase.drawingsTranslation,
    epPhase.drawingsTranslationPageRange,
    epPhase.drawingsTranslationPageCount
  )
  const drawingsArticle34AmendmentPagesValid = pageRowValid(
    epPhase.drawingsArticle34Amendment,
    epPhase.drawingsArticle34AmendmentPageRange,
    epPhase.drawingsArticle34AmendmentPageCount
  )
  const drawingsEpPhaseAmendmentsPagesValid = pageRowValid(
    epPhase.drawingsEpPhaseAmendments,
    epPhase.drawingsEpPhaseAmendmentsPageRange,
    epPhase.drawingsEpPhaseAmendmentsPageCount
  )
  const descriptionPageTotal =
    (epPhase.descriptionPctPublished
      ? parsePageCount(epPhase.descriptionPctPublishedPageCount)
      : 0) +
    (epPhase.descriptionTranslation
      ? parsePageCount(epPhase.descriptionTranslationPageCount)
      : 0) +
    (epPhase.descriptionArticle34Amendment
      ? parsePageCount(epPhase.descriptionArticle34AmendmentPageCount)
      : 0) +
    (epPhase.descriptionArticle34Translation
      ? parsePageCount(epPhase.descriptionArticle34TranslationPageCount)
      : 0) +
    (epPhase.descriptionEpPhaseAmendments
      ? parsePageCount(epPhase.descriptionEpPhaseAmendmentsPageCount)
      : 0)
  const claimsPageTotal =
    (epPhase.claimsPctPublished ? parsePageCount(epPhase.claimsPctPublishedPageCount) : 0) +
    (epPhase.claimsArticle19Amendment
      ? parsePageCount(epPhase.claimsArticle19AmendmentPageCount)
      : 0) +
    (epPhase.claimsArticle34Amendment
      ? parsePageCount(epPhase.claimsArticle34AmendmentPageCount)
      : 0) +
    (epPhase.claimsEpPhaseAmendments
      ? parsePageCount(epPhase.claimsEpPhaseAmendmentsPageCount)
      : 0)
  const drawingsPageTotal =
    (epPhase.drawingsPctPublished ? parsePageCount(epPhase.drawingsPctPublishedPageCount) : 0) +
    (epPhase.drawingsTranslation
      ? parsePageCount(epPhase.drawingsTranslationPageCount)
      : 0) +
    (epPhase.drawingsArticle34Amendment
      ? parsePageCount(epPhase.drawingsArticle34AmendmentPageCount)
      : 0) +
    (epPhase.drawingsEpPhaseAmendments
      ? parsePageCount(epPhase.drawingsEpPhaseAmendmentsPageCount)
      : 0)
  const totalFilingPages = descriptionPageTotal + claimsPageTotal + drawingsPageTotal + 1
  const claimsCountsComplete =
    parsePageCount(epPhase.claimsForEuropeanPhaseCount) > 0 &&
    parsePageCount(epPhase.claimsFeePaidCount) > 0
  const hasDescriptionPages =
    epPhase.descriptionPctPublished ||
    epPhase.descriptionTranslation ||
    epPhase.descriptionArticle34Amendment ||
    epPhase.descriptionArticle34Translation ||
    epPhase.descriptionEpPhaseAmendments
  const hasClaimsPages =
    epPhase.claimsPctPublished ||
    epPhase.claimsArticle19Amendment ||
    epPhase.claimsArticle34Amendment ||
    epPhase.claimsEpPhaseAmendments
  const documentsComplete =
    claimsCountsComplete &&
    hasDescriptionPages &&
    hasClaimsPages &&
    descriptionPctPublishedPagesValid &&
    descriptionTranslationPagesValid &&
    descriptionArticle34AmendmentPagesValid &&
    descriptionArticle34TranslationPagesValid &&
    descriptionEpPhaseAmendmentsPagesValid &&
    claimsPctPublishedPagesValid &&
    claimsArticle19AmendmentPagesValid &&
    claimsArticle34AmendmentPagesValid &&
    claimsEpPhaseAmendmentsPagesValid &&
    drawingsPctPublishedPagesValid &&
    drawingsTranslationPagesValid &&
    drawingsArticle34AmendmentPagesValid &&
    drawingsEpPhaseAmendmentsPagesValid
  const activeAdditionalDocuments = epPhase.additionalDocumentsEnabled
    ? epPhase.additionalDocuments
    : []

  const resetAdditionalDocumentModal = useCallback(() => {
    setAdditionalDocumentKind("")
    setAdditionalDocumentName("")
    setAdditionalDocumentFile(null)
    setEpPhaseUploadErrors((current) => ({ ...current, additionalDocument: "" }))
  }, [])

  const closeAdditionalDocumentModal = useCallback(() => {
    additionalDocumentUploadRequestRef.current += 1
    setAdditionalDocumentModalOpen(false)
    resetAdditionalDocumentModal()
  }, [resetAdditionalDocumentModal])

  useEffect(() => {
    if (!additionalDocumentModalOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAdditionalDocumentModal()
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [additionalDocumentModalOpen, closeAdditionalDocumentModal])


  const entryComplete =
    Boolean(referenceNumber.trim()) &&
    Boolean(epPhase.internationalPublicationLanguage) &&
    Boolean(epPhase.proceedingsLanguage) &&
    Boolean(epPhase.internationalFilingDate) &&
    !isFutureDate(epPhase.internationalFilingDate) &&
    Boolean(epPhase.pctApplicationNumber.trim()) &&
    Boolean(epPhase.internationalSearchingAuthority) &&
    (!epPhase.requestExaminationInNonOfficialLanguage ||
      Boolean(epPhase.admissibleNonEpoLanguage))
  const statesBiomSeqlComplete =
    epPhase.extensionStatesRequested ||
    epPhase.validationStatesRequested ||
    (epPhase.biologicalMaterial &&
      epPhase.biologicalMaterialDeposits.some(
        (deposit) =>
          Boolean(deposit.depositaryInstitution) &&
          Boolean(deposit.accessionNumber) &&
          (deposit.receiptCertificateStatus === "filedLater" ||
            Boolean(deposit.receiptCertificateDocument))
      )) ||
    (epPhase.sequenceListing &&
      (epPhase.sequenceListingOption === "available" ||
        (epPhase.sequenceListingOption === "attached" &&
          Boolean(epPhase.sequenceListingStandard) &&
          Boolean(epPhase.sequenceListingDocument) &&
          epPhase.sequenceListingContentConfirmed)))

  const steps: PatentFormStep[] = FORM_STEPS.map((label, index) => ({
    key: label,
    label,
    complete:
      index === 0
        ? entryComplete
        : index === 1
          ? Boolean(applicants?.filter((applicant) => !applicant.isDeleted).length) &&
            Boolean(applicants?.filter((applicant) => !applicant.isDeleted).every(isApplicantComplete))
          : index === 2
            ? statesBiomSeqlComplete
            : index === 3
              ? documentsComplete
              : label === FEES_STEP_LABEL
                ? true
                : label === SIGNATURE_STEP_LABEL
                  ? Boolean(signature?.accepted)
                  : false
  }))

  const paymentEnabled = !isLocked && steps.every((step) => step.complete)

  const setEpPhaseField = <K extends keyof EpPhaseValue>(key: K, value: EpPhaseValue[K]) => {
    setEpPhase((current) => ({ ...current, [key]: value }))
  }

  // The number of either field finds the application: the PCT application number or the WO publication number
  const handleRetrievePctData = async (number: string) => {
    setPctLookup({ loading: true, message: "", failed: false })

    try {
      const result = await lookupPctApplication(number)
      const { list, addedCount } = addRetrievedApplicants(applicants, result.applicants)

      const language = languageOption(result.publicationLanguage, pctPublicationLanguages)
      const authority = authorityOption(result.searchAuthority, SEARCH_AUTHORITIES)

      if (result.internationalFilingDate) {
        setEpPhaseField("internationalFilingDate", result.internationalFilingDate)
      }
      if (language) setEpPhaseField("internationalPublicationLanguage", language)
      if (authority) setEpPhaseField("internationalSearchingAuthority", authority)
      setApplicants(list)

      const found = result.title ? `Found: ${result.title}. ` : "Found the application. "
      const filled = describeFilled(
        [
          result.internationalFilingDate && "the international filing date",
          language && "the language of the international publication",
          authority && "the ISA"
        ].filter((item): item is string => Boolean(item))
      )
      // What the register does not tell stays for the person to choose
      const missing = [
        !language && "the language of the international publication",
        !authority && "the ISA"
      ].filter((item): item is string => Boolean(item))
      const choose = missing.length ? ` Please choose ${missing.join(" and ")} yourself.` : ""
      const people =
        addedCount > 0
          ? `${addedCount} applicant${addedCount === 1 ? " was" : "s were"} added: please open ${addedCount === 1 ? "it" : "each one"} in the next step and add the address.`
          : "No new applicants were added."

      setPctLookup({ loading: false, message: `${found}${filled}${choose} ${people}`.replace(/\s{2,}/g, " "), failed: false })
    } catch (error) {
      setPctLookup({ loading: false, message: formatApiError(error), failed: true })
    }
  }

  const renderLookupButton = (number: string) =>
    isLocked ? undefined : (
      <button
        type="button"
        onClick={() => handleRetrievePctData(number)}
        disabled={pctLookup.loading || !/\d/.test(number)}
        title="Retrieve the filing date and applicants from the EPO"
        aria-label="Retrieve the filing date and applicants from the EPO"
        className="flex w-9 items-center justify-center rounded bg-dantzBlue/85 text-white transition hover:bg-dantzBlue disabled:cursor-not-allowed disabled:opacity-40"
      >
        {pctLookup.loading ? <FiRefreshCw size={16} className="animate-spin" /> : <FiSearch size={16} />}
      </button>
    )

  const handleSignatureSaved = (nextSignatureId: string | null) => {
    setSignature((current: any) => (current ? { ...current, _id: nextSignatureId } : current))
  }

  const toggleEpPhaseState = (
    listKey: "selectedExtensionStates" | "selectedValidationStates",
    code: string,
    checked: boolean
  ) => {
    setEpPhase((current) => {
      const currentStates = current[listKey]
      return {
        ...current,
        [listKey]: checked
          ? Array.from(new Set([...currentStates, code]))
          : currentStates.filter((state) => state !== code)
      }
    })
  }

  const uploadEpPhaseFile = async (
    file: File,
    type: PatentDocumentType,
    uploadKey: string,
    onUploaded: (document: EpPhaseUploadDocument) => void,
    validateFile: (file: File) => string | null = validatePatentPdfFile
  ) => {
    const validationError = validateFile(file)
    if (validationError) {
      setEpPhaseUploadErrors((current) => ({
        ...current,
        [uploadKey]: validationError
      }))
      return
    }

    setUploadingEpPhaseDocument(uploadKey)
    setEpPhaseUploadErrors((current) => ({ ...current, [uploadKey]: "" }))

    try {
      const uploaded = await uploadPatentDocument(file, type)
      onUploaded({
        type,
        fileName: uploaded.fileName,
        epoDocumentId: uploaded.documentId
      })
    } catch (error) {
      setEpPhaseUploadErrors((current) => ({
        ...current,
        [uploadKey]: formatApiError(error, "Upload failed. Please try again.")
      }))
    } finally {
      setUploadingEpPhaseDocument(null)
    }
  }

  const uploadAdditionalDocumentFile = (file: File) => {
    const requestId = additionalDocumentUploadRequestRef.current + 1
    additionalDocumentUploadRequestRef.current = requestId

    void uploadEpPhaseFile(file, "OTHER", "additionalDocument", (uploaded) => {
      if (additionalDocumentUploadRequestRef.current === requestId) {
        setAdditionalDocumentFile(uploaded)
      }
    })
  }

  const addAdditionalDocument = () => {
    if (!additionalDocumentKind || !additionalDocumentName.trim() || !additionalDocumentFile) return

    setEpPhase((current) => ({
      ...current,
      additionalDocuments: [
        ...current.additionalDocuments,
        {
          id: crypto.randomUUID(),
          documentKind: additionalDocumentKind,
          customName: additionalDocumentName.trim(),
          document: additionalDocumentFile
        }
      ]
    }))
    closeAdditionalDocumentModal()
  }

  const toggleAdditionalDocumentSelection = (id: string, checked: boolean) => {
    setSelectedAdditionalDocumentIds((current) =>
      checked
        ? Array.from(new Set([...current, id]))
        : current.filter((selectedId) => selectedId !== id)
    )
  }

  const removeSelectedAdditionalDocuments = () => {
    setEpPhase((current) => ({
      ...current,
      additionalDocuments: current.additionalDocuments.filter(
        (document) => !selectedAdditionalDocumentIds.includes(document.id)
      )
    }))
    setSelectedAdditionalDocumentIds([])
  }

  const updateBiologicalMaterialDeposit = (
    depositId: string,
    changes: Partial<BiologicalMaterialDeposit>
  ) => {
    setEpPhase((current) => ({
      ...current,
      biologicalMaterialDeposits: current.biologicalMaterialDeposits.map((deposit) =>
        deposit.id === depositId ? { ...deposit, ...changes } : deposit
      )
    }))
  }

  const removeBiologicalMaterialDeposit = (depositId: string) => {
    setEpPhase((current) => ({
      ...current,
      biologicalMaterialDeposits: current.biologicalMaterialDeposits.filter(
        (deposit) => deposit.id !== depositId
      )
    }))
  }

  const saveEpPhaseApplication = async () => {
    if (isLocked) throw new Error("Submitted applications cannot be edited.")

    const epPhaseForSave = {
      ...epPhase,
      descriptionPageCount: String(descriptionPageTotal),
      claimsPageCount: String(claimsPageTotal),
      drawingPageCount: String(drawingsPageTotal)
    }

    const nextDraftId = await savePatentDraft({
      procedureType: "EP1200",
      epPhase: epPhaseForSave,
      referenceNumber,
      title: "",
      titleTranslations: {},
      applicants,
      inventors: [],
      priorities: [],
      documents: [],
      filingLanguage: epPhase.proceedingsLanguage,
      applicationLanguage: epPhase.internationalPublicationLanguage,
      examinationInNonOfficialLanguage: false,
      declarations: null as any,
      claimsFiledLater: false,
      translationFiledLater: false,
      signature,
      draftId,
      signatureId,
      onSignatureSaved: handleSignatureSaved,
      onDraftSaved: setDraftId
    })

    setDraftId(nextDraftId)
    setFeeRefreshToken((token) => token + 1)
    if (!statusMustStayLocked) {
      await updatePatentApplicationStatus(nextDraftId, "DRAFT").catch(() => {})
      setApplicationStatus("DRAFT")
    }
    return nextDraftId
  }

  const handleSave = () => runSaving(async () => {
    try {
      await saveEpPhaseApplication()
      setValidationError("")
      setSuccessMessage("EP phase entry draft saved successfully.")
    } catch (error) {
      setSuccessMessage("")
      setValidationError(
        formatApiError(
          error,
          "The EP phase entry draft could not be saved. Please reload the page and try again."
        )
      )
    }
  })

  const handleProceedToPayment = () => {
    if (!paymentEnabled) return
    return runSaving(async () => {
      try {
        const nextDraftId = await saveEpPhaseApplication()

        await validatePatentApplication(nextDraftId)
        await updatePatentApplicationStatus(nextDraftId, "VALIDATED")
        setApplicationStatus("VALIDATED")
        setValidationError("")
        setSuccessMessage("")
        navigate(`/patent-applications/${nextDraftId}/payment`)
      } catch (error) {
        console.error("EP phase entry could not be validated:", error)
        setValidationError(
          formatApiError(
            error,
            "The EP phase entry could not be validated. Please review the highlighted fields and try again."
          )
        )
      }
    })
  }

  const handleReset = () => runSaving(async () => {
    try {
      const emptyEpPhase = { ...EMPTY_EP_PHASE }
      const deletedApplicants = (applicants || []).map((applicant) => ({
        ...applicant,
        isDeleted: true
      }))
      const nextDraftId = await savePatentDraft({
        procedureType: "EP1200",
        epPhase: emptyEpPhase,
        referenceNumber: "",
        title: "",
        titleTranslations: {},
        applicants: deletedApplicants,
        inventors: [],
        priorities: [],
        documents: [],
        filingLanguage: "",
        applicationLanguage: "",
        examinationInNonOfficialLanguage: false,
        declarations: null as any,
        claimsFiledLater: false,
        translationFiledLater: false,
        signature: null,
        draftId,
        signatureId,
        onSignatureSaved: handleSignatureSaved,
        onDraftSaved: setDraftId
      })

      resetForm()
      setDraftId(nextDraftId)
      setReferenceNumber("")
      setApplicants([])
      setEpPhase(emptyEpPhase)
      setSignature(null)
      setEpPhaseUploadErrors({})
      setUploadingEpPhaseDocument(null)
      setSelectedAdditionalDocumentIds([])
      closeAdditionalDocumentModal()
      setActiveAmendmentUpload(null)
      setAmendmentMenuOpen(false)
      setStep(0)
      setApplicationStatus("DRAFT")
      setValidationError("")
      setSuccessMessage("EP phase entry draft reset successfully.")
    } catch (error) {
      setSuccessMessage("")
      setValidationError(
        formatApiError(
          error,
          "The EP phase entry draft could not be reset. Please reload the page and try again."
        )
      )
    }
  })

  useEffect(() => {
    if (!amendmentMenuOpen) return

    const handleDocumentClick = (event: MouseEvent) => {
      if (!amendmentMenuRef.current?.contains(event.target as Node)) {
        setAmendmentMenuOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setAmendmentMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleDocumentClick)
    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [amendmentMenuOpen])

  const goToStep = (index: number) => {
    if (index > 3 && !documentsComplete) {
      setSuccessMessage("")
      setValidationError(
        "Please select the relevant description and claims documents and enter valid page ranges and page counts before continuing."
      )
      return
    }

    setValidationError("")
    void goToStepBase(index, saveEpPhaseApplication, Boolean(draftId || referenceNumber))
  }

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }

    async function load() {
      try {
        const app = await getPatentApplicationById(id as string)
        if (app.procedureType && app.procedureType !== "EP1200") {
          navigate(
            `/patent-applications/${id}/${isViewMode ? "view" : "edit"}`,
            { replace: true }
          )
          return
        }

        setOrderingParty(typeof app.userId === "object" ? app.userId : null)
        setDraftId(app._id)
        setApplicationStatus(app.status || "DRAFT")
        setReferenceNumber(app.referenceNumber?.referenceNumber || "")
        setApplicants(app.applicants || [])
        setEpPhase({
          ...EMPTY_EP_PHASE,
          ...(app.epPhase || {}),
          internationalFilingDate: toDateInput(app.epPhase?.internationalFilingDate),
          biologicalMaterialDeposits: (app.epPhase?.biologicalMaterialDeposits || []).map(
            (deposit: BiologicalMaterialDeposit) => ({
              ...deposit,
              id: deposit.id || crypto.randomUUID()
            })
          ),
          additionalDocuments: (app.epPhase?.additionalDocuments || []).map(
            (document: AdditionalDocument) => ({
              ...document,
              id: document.id || crypto.randomUUID()
            })
          )
        })
        if (app.signature) {
          setSignature({ ...app.signature, accepted: true })
        } else {
          setSignature(null)
        }
      } catch (error) {
        setValidationError(
          formatApiError(
            error,
            "The EP phase entry draft could not be loaded. Please reload the page and try again."
          )
        )
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [id, isViewMode, navigate])

  if (loading) {
    return (
      <AppLayout title="EP Phase Entry" wide>
        <div className="flex justify-center py-32 text-gray-600">
          <SpinnerText>Loading application...</SpinnerText>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="EP Phase Entry" wide>
      {validationError && (
        <StatusToast
          type="error"
          message={validationError}
          onClose={() => setValidationError("")}
        />
      )}

      {successMessage && (
        <StatusToast
          type="success"
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}

      {savingDraft && <StatusToast type="loading" message="Saving your EP phase entry…" />}

      <div className="sticky top-0 z-30 border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <PatentFormStepper
            steps={steps}
            current={step}
            onSelect={goToStep}
            disabled={savingDraft}
          />
        </div>
      </div>

      <div className="flex justify-center py-12 pb-28">
        <div className="grid w-full max-w-7xl grid-cols-[minmax(0,1fr)_18rem] gap-7">
          <div className="rounded-2xl border bg-white p-5 sm:p-8 xl:p-12 shadow-sm">
            <div className={`flex-col gap-10 ${step === 0 ? "flex" : "hidden"}`}>
              <div className="border-b border-gray-200 pb-6">
                <h1 className="font-heading text-xl font-semibold text-dantzBlue">
                  {PATENT_PROCEDURE_LABELS.EP1200}
                </h1>
              </div>

              <section className="space-y-6 border-b border-gray-200 pb-8">
                <h2 className="font-heading text-base font-semibold text-dantzBlue">
                  Application details
                </h2>

                <ReferenceNumberSection
                  value={referenceNumber}
                  onChange={setReferenceNumber}
                  disabled={isLocked}
                />

                <div className="space-y-3">
                  <label className="flex items-center gap-3 text-sm text-dantzBlue">
                    <input
                      type="radio"
                      checked={epPhase.entryRole === "DESIGNATED_OFFICE"}
                      disabled={isLocked}
                      onChange={() => setEpPhaseField("entryRole", "DESIGNATED_OFFICE")}
                      className="h-4 w-4 border-gray-300 text-dantzBlue focus:ring-dantzBlue"
                    />
                    EPO as designated Office
                  </label>
                  <label className="flex items-center gap-3 text-sm text-dantzBlue">
                    <input
                      type="radio"
                      checked={epPhase.entryRole === "ELECTED_OFFICE"}
                      disabled={isLocked}
                      onChange={() => setEpPhaseField("entryRole", "ELECTED_OFFICE")}
                      className="h-4 w-4 border-gray-300 text-dantzBlue focus:ring-dantzBlue"
                    />
                    EPO as elected Office
                  </label>
                </div>

                <div className="space-y-4">
                  <p className="text-sm font-medium text-dantzBlue">
                    Request for examination of the application under Article 94 EPC
                  </p>
                  <CheckboxRow
                    label="Request for examination in an admissible non-EPO language"
                    checked={epPhase.requestExaminationInNonOfficialLanguage}
                    disabled={isLocked}
                    onChange={(value) => {
                      setEpPhaseField("requestExaminationInNonOfficialLanguage", value)
                      if (!value) {
                        setEpPhaseField("admissibleNonEpoLanguage", "")
                        setEpPhaseField("feeReductionNaturalPersonOrEntity", false)
                      }
                    }}
                  />
                  {epPhase.requestExaminationInNonOfficialLanguage && (
                    <SelectField
                      label="Admissible non-EPO language"
                      required
                      value={epPhase.admissibleNonEpoLanguage}
                      options={epoAdmissibleNonOfficialLanguages}
                      disabled={isLocked}
                      onChange={(value) => setEpPhaseField("admissibleNonEpoLanguage", value)}
                    />
                  )}
                  {epPhase.requestExaminationInNonOfficialLanguage && (
                    <CheckboxRow
                      label="The applicant requests a fee reduction under Rule 7a(1) EPC and is a natural person or an entity under Rule 7a(2) EPC."
                      checked={epPhase.feeReductionNaturalPersonOrEntity}
                      disabled={isLocked}
                      onChange={(value) => setEpPhaseField("feeReductionNaturalPersonOrEntity", value)}
                    />
                  )}
                  <CheckboxRow
                    label="The applicant requests a fee reduction under Rule 7a(3) EPC and is a small entity under Rule 7a(3) EPC."
                    checked={epPhase.feeReductionSmallEntity}
                    disabled={isLocked}
                    onChange={(value) => setEpPhaseField("feeReductionSmallEntity", value)}
                  />
                </div>
              </section>

              <section className="space-y-5 border-b border-gray-200 pb-8">
                <h2 className="font-heading text-base font-semibold text-dantzBlue">
                  International phase details
                </h2>

                <TextField
                  label="PCT application number"
                  required
                  value={epPhase.pctApplicationNumber}
                  placeholder="PCT"
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("pctApplicationNumber", value)}
                  action={renderLookupButton(epPhase.pctApplicationNumber)}
                />

                <TextField
                  label="International publication number"
                  value={epPhase.internationalPublicationNumber}
                  placeholder="WO"
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("internationalPublicationNumber", value)}
                  action={renderLookupButton(epPhase.internationalPublicationNumber)}
                />

                {pctLookup.message && (
                  <p className={`-mt-3 text-sm ${pctLookup.failed ? "text-red-600" : "text-gray-700"}`}>
                    {pctLookup.message}
                  </p>
                )}

                <SelectField
                  label="Language of the international publication"
                  required
                  value={epPhase.internationalPublicationLanguage}
                  options={pctPublicationLanguages}
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("internationalPublicationLanguage", value)}
                />

                <SelectField
                  label="Language of the proceedings before the EPO"
                  required
                  value={epPhase.proceedingsLanguage}
                  options={PROCEEDINGS_LANGUAGES}
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("proceedingsLanguage", value)}
                />

                <TextField
                  label="International filing date"
                  required
                  type="date"
                  max={todayInputValue()}
                  value={epPhase.internationalFilingDate}
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("internationalFilingDate", value)}
                />

                <TextField
                  label="EP application number"
                  value={epPhase.epApplicationNumber}
                  placeholder="EP"
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("epApplicationNumber", value)}
                />

                <SelectField
                  label="International Searching Authority (ISA)"
                  required
                  value={epPhase.internationalSearchingAuthority}
                  options={SEARCH_AUTHORITIES}
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("internationalSearchingAuthority", value)}
                />
              </section>

              <section className="space-y-4">
                <h2 className="font-heading text-base font-semibold text-dantzBlue">
                  Acceleration of the procedure
                </h2>
                <CheckboxRow
                  label="Request for early processing of the application under Article 23(2) PCT / Article 40(2) PCT"
                  checked={epPhase.acceleratedProcessing}
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("acceleratedProcessing", value)}
                  muted
                />
                <CheckboxRow
                  label="Waiver of the invitation to indicate whether the application is to be maintained (Rule 70(2) EPC)"
                  checked={epPhase.waiveInvitationToMaintainApplication}
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("waiveInvitationToMaintainApplication", value)}
                />
                <CheckboxRow
                  label="Waiver of the communication under Rule 161(1) or (2) EPC and Rule 162 EPC"
                  checked={epPhase.waiveRule161Communication}
                  disabled={isLocked}
                  onChange={(value) => setEpPhaseField("waiveRule161Communication", value)}
                />
              </section>
            </div>

            {step === 1 && (
              <div className="flex-col gap-10 flex">
                <PatentApplicantAndRepresentative
                orderingParty={orderingParty}
                  onApplicantChange={setApplicants}
                  initialData={applicants ?? []}
                  disabled={isLocked}
                />
              </div>
            )}

            {step === 2 && (
              <div className="flex-col gap-10 flex">
                <div className="border-b border-gray-200 pb-6">
                  <h1 className="font-heading text-xl font-semibold text-dantzBlue">
                    States, biological material, sequence listing
                  </h1>
                </div>

                <section className="space-y-5 border-b border-gray-200 pb-8">
                  <h2 className="font-heading text-base font-semibold text-dantzBlue">
                    States
                  </h2>
                  <p className="max-w-5xl text-sm leading-6 text-dantzBlue">
                    All contracting states which belonged to the EPC when the PCT
                    application was filed and which were designated in the
                    application are deemed to be designated (Article 79(1) EPC).
                  </p>
                  <CheckboxRow
                    label="Request extension to extension states"
                    checked={epPhase.extensionStatesRequested}
                    disabled={isLocked}
                    onChange={(value) => {
                      setEpPhaseField("extensionStatesRequested", value)
                      if (!value) {
                        setEpPhase((current) => ({
                          ...current,
                          extensionStateMode: "all",
                          selectedExtensionStates: []
                        }))
                      }
                    }}
                  />
                  {epPhase.extensionStatesRequested && (
                    <StateRequestPanel
                      type="extension"
                      mode={epPhase.extensionStateMode}
                      selectedStates={epPhase.selectedExtensionStates}
                      disabled={isLocked}
                      onModeChange={(mode) => {
                        setEpPhase((current) => ({
                          ...current,
                          extensionStateMode: mode,
                          selectedExtensionStates:
                            mode === "all" ? [] : current.selectedExtensionStates
                        }))
                      }}
                      onStateToggle={(code, checked) =>
                        toggleEpPhaseState("selectedExtensionStates", code, checked)
                      }
                    />
                  )}

                  <CheckboxRow
                    label="Request validation in validation states"
                    checked={epPhase.validationStatesRequested}
                    disabled={isLocked}
                    onChange={(value) => {
                      setEpPhaseField("validationStatesRequested", value)
                      if (!value) {
                        setEpPhase((current) => ({
                          ...current,
                          validationStateMode: "all",
                          selectedValidationStates: []
                        }))
                      }
                    }}
                  />
                  {epPhase.validationStatesRequested && (
                    <StateRequestPanel
                      type="validation"
                      mode={epPhase.validationStateMode}
                      selectedStates={epPhase.selectedValidationStates}
                      disabled={isLocked}
                      onModeChange={(mode) => {
                        setEpPhase((current) => ({
                          ...current,
                          validationStateMode: mode,
                          selectedValidationStates:
                            mode === "all" ? [] : current.selectedValidationStates
                        }))
                      }}
                      onStateToggle={(code, checked) =>
                        toggleEpPhaseState("selectedValidationStates", code, checked)
                      }
                    />
                  )}
                </section>

                <section className="space-y-5 border-b border-gray-200 pb-8">
                  <h2 className="font-heading text-base font-semibold text-dantzBlue">
                    Biological material
                  </h2>
                  <CheckboxRow
                    label="Biological material"
                    checked={epPhase.biologicalMaterial}
                    disabled={isLocked}
                    onChange={(value) => {
                      setEpPhaseField("biologicalMaterial", value)
                      if (!value) {
                        setEpPhase((current) => ({
                          ...current,
                          biologicalMaterialDeposits: []
                        }))
                      }
                    }}
                  />
                  {epPhase.biologicalMaterial && (
                    <div className="overflow-hidden border border-gray-100 bg-white">
                      <div className="flex items-center justify-between bg-gray-100 px-4 py-3">
                        <div className="flex items-center gap-3 text-sm text-dantzBlue">
                          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e6edf5] font-medium">
                            {epPhase.biologicalMaterialDeposits.length}
                          </span>
                          <span>
                            Biological material deposit
                            {epPhase.biologicalMaterialDeposits.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            disabled={isLocked}
                            onClick={() =>
                              setEpPhase((current) => ({
                                ...current,
                                biologicalMaterialDeposits: [
                                  ...current.biologicalMaterialDeposits,
                                  emptyBiologicalMaterialDeposit()
                                ]
                              }))
                            }
                            className="bg-dantzBlue px-5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                      <div className="min-h-28 px-5 py-5">
                        {epPhase.biologicalMaterialDeposits.length === 0 ? (
                          <p className="text-sm italic text-gray-400">
                            No biological material deposits found
                          </p>
                        ) : (
                          <div className="grid gap-6">
                            {epPhase.biologicalMaterialDeposits.map((deposit, index) => (
                              <div key={deposit.id} className="border-l-4 border-dantzBlue bg-gray-50 px-5 py-4">
                                <div className="mb-4 flex items-center justify-between">
                                  <div className="text-sm font-medium text-dantzBlue">
                                    Biological material deposit {index + 1}
                                  </div>
                                  <button
                                    type="button"
                                    disabled={isLocked}
                                    onClick={() => removeBiologicalMaterialDeposit(deposit.id)}
                                    className="text-sm font-medium text-red-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    Remove
                                  </button>
                                </div>
                                <p className="mb-4 text-sm text-dantzBlue">
                                  The invention relates to or uses biological material
                                  deposited under Rule 31 EPC.
                                </p>

                                <div className="grid gap-5">
                                  <TextField
                                    label="Reference"
                                    value={deposit.reference}
                                    disabled={isLocked}
                                    onChange={(value) =>
                                      updateBiologicalMaterialDeposit(deposit.id, { reference: value })
                                    }
                                  />
                                  <SelectField
                                    label="Depositary institution"
                                    value={deposit.depositaryInstitution}
                                    options={DEPOSITARY_INSTITUTIONS}
                                    disabled={isLocked}
                                    onChange={(value) =>
                                      updateBiologicalMaterialDeposit(deposit.id, {
                                        depositaryInstitution: value
                                      })
                                    }
                                  />
                                  <TextField
                                    label="Accession number"
                                    value={deposit.accessionNumber}
                                    disabled={isLocked}
                                    onChange={(value) =>
                                      updateBiologicalMaterialDeposit(deposit.id, {
                                        accessionNumber: value
                                      })
                                    }
                                  />

                                  <CheckboxRow
                                    label="The depositary institution and the accession number or, if the accession number is not yet known, the depositary institution and the reference assigned by the depositor are included in the application documents."
                                    checked={deposit.depositInformationInApplication}
                                    disabled={isLocked}
                                    onChange={(checked) =>
                                      updateBiologicalMaterialDeposit(deposit.id, {
                                        depositInformationInApplication: checked,
                                        pageNumber: checked ? deposit.pageNumber : "",
                                        lineNumber: checked ? deposit.lineNumber : ""
                                      })
                                    }
                                  />
                                  {deposit.depositInformationInApplication && (
                                    <div className="ml-7 grid gap-4 sm:grid-cols-2">
                                      <TextField
                                        label="Page(s)"
                                        value={deposit.pageNumber}
                                        disabled={isLocked}
                                        onChange={(value) =>
                                          updateBiologicalMaterialDeposit(deposit.id, {
                                            pageNumber: value
                                          })
                                        }
                                      />
                                      <TextField
                                        label="Line(s)"
                                        value={deposit.lineNumber}
                                        disabled={isLocked}
                                        onChange={(value) =>
                                          updateBiologicalMaterialDeposit(deposit.id, {
                                            lineNumber: value
                                          })
                                        }
                                      />
                                    </div>
                                  )}

                                  <div className="border-t border-gray-200 pt-4">
                                    <h3 className="mb-2 text-sm font-semibold text-dantzBlue">
                                      Receipt certificate
                                    </h3>
                                    <p className="mb-3 text-sm text-dantzBlue">
                                      A copy of the receipt issued by the depositary
                                      institution.
                                    </p>
                                    <div className="mb-4 flex flex-wrap gap-x-8 gap-y-3">
                                      <RadioRow
                                        name={`biom-receipt-${index}`}
                                        label="is attached"
                                        checked={deposit.receiptCertificateStatus === "attached"}
                                        disabled={isLocked}
                                        onChange={() =>
                                          updateBiologicalMaterialDeposit(deposit.id, {
                                            receiptCertificateStatus: "attached"
                                          })
                                        }
                                      />
                                      <RadioRow
                                        name={`biom-receipt-${index}`}
                                        label="will be filed later"
                                        checked={deposit.receiptCertificateStatus === "filedLater"}
                                        disabled={isLocked}
                                        onChange={() =>
                                          updateBiologicalMaterialDeposit(deposit.id, {
                                            receiptCertificateStatus: "filedLater"
                                          })
                                        }
                                      />
                                    </div>
                                    {deposit.receiptCertificateStatus === "attached" && (
                                      <EpPhaseAttachmentTable
                                        label="Receipt certificate"
                                        document={deposit.receiptCertificateDocument}
                                        uploadVisible={Boolean(
                                          deposit.receiptCertificateUploadRequested
                                        )}
                                        uploading={
                                          uploadingEpPhaseDocument === `${deposit.id}:receiptCertificate`
                                        }
                                        error={epPhaseUploadErrors[`${deposit.id}:receiptCertificate`] || ""}
                                        disabled={isLocked}
                                        onAdd={() =>
                                          updateBiologicalMaterialDeposit(deposit.id, {
                                            receiptCertificateUploadRequested: true
                                          })
                                        }
                                        onSelect={(file) =>
                                          uploadEpPhaseFile(
                                            file,
                                            "BIOLOGICAL_MATERIAL",
                                            `${deposit.id}:receiptCertificate`,
                                            (uploaded) =>
                                              updateBiologicalMaterialDeposit(deposit.id, {
                                                receiptCertificateUploadRequested: true,
                                                receiptCertificateDocument: uploaded
                                              })
                                          )
                                        }
                                        onDelete={() =>
                                          updateBiologicalMaterialDeposit(deposit.id, {
                                            receiptCertificateUploadRequested: false,
                                            receiptCertificateDocument: null
                                          })
                                        }
                                      />
                                    )}
                                  </div>

                                  <div className="border-t border-gray-200 pt-4">
                                    <CheckboxRow
                                      label="Waiver under Rule 33(2) EPC for a request to issue a sample of the biological material is attached"
                                      checked={deposit.rule33WaiverAttached}
                                      disabled={isLocked}
                                      onChange={(checked) =>
                                        updateBiologicalMaterialDeposit(deposit.id, {
                                          rule33WaiverAttached: checked,
                                          rule33WaiverUploadRequested: checked
                                            ? deposit.rule33WaiverUploadRequested
                                            : false,
                                          rule33WaiverDocument: checked
                                            ? deposit.rule33WaiverDocument
                                            : null
                                        })
                                      }
                                    />
                                    {deposit.rule33WaiverAttached && (
                                      <div className="mt-4">
                                        <EpPhaseAttachmentTable
                                          label="Rule 33(2) EPC waiver"
                                          document={deposit.rule33WaiverDocument}
                                          uploadVisible={Boolean(deposit.rule33WaiverUploadRequested)}
                                          uploading={
                                            uploadingEpPhaseDocument === `${deposit.id}:rule33Waiver`
                                          }
                                          error={epPhaseUploadErrors[`${deposit.id}:rule33Waiver`] || ""}
                                          disabled={isLocked}
                                          onAdd={() =>
                                            updateBiologicalMaterialDeposit(deposit.id, {
                                              rule33WaiverUploadRequested: true
                                            })
                                          }
                                          onSelect={(file) =>
                                            uploadEpPhaseFile(
                                              file,
                                              "BIOLOGICAL_MATERIAL",
                                              `${deposit.id}:rule33Waiver`,
                                              (uploaded) =>
                                                updateBiologicalMaterialDeposit(deposit.id, {
                                                  rule33WaiverUploadRequested: true,
                                                  rule33WaiverDocument: uploaded
                                                })
                                            )
                                          }
                                          onDelete={() =>
                                            updateBiologicalMaterialDeposit(deposit.id, {
                                              rule33WaiverUploadRequested: false,
                                              rule33WaiverDocument: null
                                            })
                                          }
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>

                <section className="space-y-5">
                  <h2 className="font-heading text-base font-semibold text-dantzBlue">
                    Sequence listing
                  </h2>
                  <CheckboxRow
                    label="The PCT application discloses nucleotide and/or amino acid sequences."
                    checked={epPhase.sequenceListing}
                    disabled={isLocked}
                    onChange={(value) => {
                      setEpPhaseField("sequenceListing", value)
                      if (!value) {
                        setEpPhase((current) => ({
                          ...current,
                          sequenceListingOption: "",
                          sequenceListingStandard: "",
                          sequenceListingDocument: null,
                          sequenceListingContentConfirmed: false
                        }))
                      }
                    }}
                  />
                  {epPhase.sequenceListing && (
                    <div className="space-y-4 bg-gray-50 px-5 py-4">
                      <RadioRow
                        name="ep1200-sequence-listing-option"
                        label="The sequence listing was filed under Rule 5.2(a) PCT or Rule 13ter.1(a) PCT with the EPO as ISA or made available to the EPO in computer-readable form according to WIPO Standard ST.25."
                        checked={epPhase.sequenceListingOption === "available"}
                        disabled={isLocked}
                        onChange={() =>
                          setEpPhase((current) => ({
                            ...current,
                            sequenceListingOption: "available",
                            sequenceListingStandard: "",
                            sequenceListingDocument: null,
                            sequenceListingContentConfirmed: false
                          }))
                        }
                      />
                      <RadioRow
                        name="ep1200-sequence-listing-option"
                        label="The sequence listing is attached."
                        checked={epPhase.sequenceListingOption === "attached"}
                        disabled={isLocked}
                        onChange={() =>
                          setEpPhase((current) => ({
                            ...current,
                            sequenceListingOption: "attached",
                            sequenceListingContentConfirmed: false
                          }))
                        }
                      />
                      {epPhase.sequenceListingOption === "attached" && (
                        <div className="ml-7 space-y-4">
                          <div>
                            <label className="mb-1 block text-sm font-medium text-dantzBlue">
                              Sequence listing document type
                            </label>
                            <select
                              value={epPhase.sequenceListingStandard}
                              disabled={isLocked}
                              onChange={(event) => {
                                const standard = event.target.value as "" | "ST25" | "ST26"
                                setEpPhase((current) => ({
                                  ...current,
                                  sequenceListingStandard: standard,
                                  sequenceListingDocument: null
                                }))
                              }}
                              className={fieldClass(false, isLocked)}
                            >
                              <option value="">- Select -</option>
                              <option value="ST25">
                                Document for sequence listing according to ST.25
                              </option>
                              <option value="ST26">
                                Document for sequence listing according to ST.26
                              </option>
                            </select>
                          </div>
                          {epPhase.sequenceListingStandard && (
                            <FileUploadSlot
                              label="Sequence listing"
                              fileName={epPhase.sequenceListingDocument?.fileName}
                              documentId={epPhase.sequenceListingDocument?.epoDocumentId}
                              uploading={uploadingEpPhaseDocument === "sequenceListing"}
                              error={epPhaseUploadErrors.sequenceListing || ""}
                              disabled={isLocked}
                              accept={SEQUENCE_LISTING_FILE_TYPES[epPhase.sequenceListingStandard].extensions.join(",")}
                              helperText={`${SEQUENCE_LISTING_FILE_TYPES[epPhase.sequenceListingStandard].label}, max. 20 MB`}
                              onSelect={(file) =>
                                uploadEpPhaseFile(
                                  file,
                                  "SEQUENCE_LISTING",
                                  "sequenceListing",
                                  (uploaded) => setEpPhaseField("sequenceListingDocument", uploaded),
                                  validatePatentSequenceListingFile(epPhase.sequenceListingStandard as "ST25" | "ST26")
                                )
                              }
                              onDelete={() => setEpPhaseField("sequenceListingDocument", null)}
                            />
                          )}
                          <CheckboxRow
                            label="The sequence listing does not go beyond the content of the application as originally filed."
                            checked={epPhase.sequenceListingContentConfirmed}
                            disabled={isLocked}
                            onChange={(checked) =>
                              setEpPhaseField("sequenceListingContentConfirmed", checked)
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}

            {step === 3 && (
              <div className="flex-col gap-10 flex">
                <div className="border-b border-gray-200 pb-6">
                  <h1 className="font-heading text-xl font-semibold text-dantzBlue">
                    Documents
                  </h1>
                </div>

                <section className="border-b border-gray-200 pb-8">
                  <h2 className="font-heading text-base font-semibold text-dantzBlue">
                    Procedural documents
                  </h2>
                  <div className="mt-4 space-y-5 rounded-md bg-gray-50 p-4 text-sm text-dantzBlue">
                    <p>
                      The proceedings before the EPO are to be based on the following documents:
                    </p>
                    <p>
                      the application documents published by WIPO. Claims amended under Article 19 PCT
                      replace the originally filed claims.
                    </p>
                    <CheckboxRow
                      label="I will attach amended replacement pages, if applicable"
                      checked={epPhase.amendedReplacementPagesAttached}
                      disabled={isLocked}
                      onChange={(checked) =>
                        setEpPhaseField("amendedReplacementPagesAttached", checked)
                      }
                    />
                    <CheckboxRow
                      label="I will attach observations or comments on the written opinion established by the EPO as ISA and/or observations or comments on the explanations in the supplementary international search report established by the EPO as SISA under Rule 45bis.7(e) PCT, if applicable"
                      checked={epPhase.isaObservationsAttached}
                      disabled={isLocked}
                      onChange={(checked) =>
                        setEpPhaseField("isaObservationsAttached", checked)
                      }
                    />
                    <div className="border-t border-gray-200 pt-5">
                      <h3 className="mb-4 font-heading text-sm font-semibold text-dantzBlue">
                        Declaration on the translation of the priority application(s)
                      </h3>
                      <CheckboxRow
                        label="The PCT application as originally filed is a complete translation of the earlier application under Rule 53(3) EPC."
                        checked={epPhase.priorityApplicationTranslationComplete}
                        disabled={isLocked}
                        onChange={(checked) =>
                          setEpPhaseField("priorityApplicationTranslationComplete", checked)
                        }
                      />
                    </div>
                  </div>
                </section>

                <section className="border-b border-gray-200 pb-8">
                  <div className="mb-3 flex items-center justify-between gap-4">
                    <h2 className="font-heading text-base font-semibold text-dantzBlue">
                      Amendments and translations - files
                    </h2>
                  </div>

                  <div className="border border-gray-200 bg-white">
                    <div className="flex items-center justify-between gap-4 bg-gray-100 px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3 text-sm font-medium text-dantzBlue">
                        <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-slate-200 px-2 font-semibold text-dantzBlue">
                          {[epPhase.priorityTranslationDocument, epPhase.internationalApplicationTranslationDocument, epPhase.iperAnnexTranslationDocument, epPhase.amendedClaimsTranslationDocument, epPhase.amendedDescriptionDocument, epPhase.amendedClaimsDocument, epPhase.amendedDrawingsDocument, epPhase.unconvertedArchiveDocument].filter(Boolean).length}
                        </span>
                        <span>Amendment(s)/translation(s)</span>
                      </div>

                      <div className="flex shrink-0 items-center gap-4">
                        <div ref={amendmentMenuRef} className="relative">
                          <button
                            type="button"
                            disabled={isLocked}
                            onClick={() => setAmendmentMenuOpen((open) => !open)}
                            aria-expanded={amendmentMenuOpen}
                            className="inline-flex items-center gap-2 rounded-lg bg-dantzBlue px-5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            Add
                            {amendmentMenuOpen ? (
                              <FiChevronUp className="h-4 w-4" aria-hidden="true" />
                            ) : (
                              <FiChevronDown className="h-4 w-4" aria-hidden="true" />
                            )}
                          </button>

                          {amendmentMenuOpen && !isLocked && (
                            <div className="absolute right-0 z-10 mt-0 w-80 overflow-hidden bg-dantzBlue text-sm text-white shadow-lg">
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveAmendmentUpload("priorityTranslation")
                                  setAmendmentMenuOpen(false)
                                }}
                                className="block w-full px-5 py-3 text-left hover:bg-slate-700"
                              >
                                Translation of the priority application(s)
                              </button>
                              {epPhase.amendedReplacementPagesAttached &&
                                AMENDMENT_UPLOADS.map((upload) => (
                                  <button
                                    key={upload.key}
                                    type="button"
                                    onClick={() => {
                                      setActiveAmendmentUpload(upload.key)
                                      setAmendmentMenuOpen(false)
                                    }}
                                    className="block w-full px-5 py-3 text-left hover:bg-slate-700"
                                  >
                                    {upload.label}
                                  </button>
                                ))}
                              {TRANSLATION_UPLOADS.filter((upload) =>
                                upload.key === "iperAnnexTranslation"
                                  ? Boolean(epPhase.internationalPublicationLanguage) &&
                                    !EPO_OFFICIAL_LANGUAGES.includes(epPhase.internationalPublicationLanguage)
                                  : epPhase.entryRole === "DESIGNATED_OFFICE"
                              ).map((upload) => (
                                <button
                                  key={upload.key}
                                  type="button"
                                  onClick={() => {
                                    setActiveAmendmentUpload(upload.key)
                                    setAmendmentMenuOpen(false)
                                  }}
                                  className="block w-full px-5 py-3 text-left hover:bg-slate-700"
                                >
                                  {upload.label}
                                </button>
                              ))}
                              {!EPO_OFFICIAL_LANGUAGES.includes(epPhase.internationalPublicationLanguage) &&
                                epPhase.internationalPublicationLanguage && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActiveAmendmentUpload("internationalTranslation")
                                      setAmendmentMenuOpen(false)
                                    }}
                                    className="block w-full px-5 py-3 text-left hover:bg-slate-700"
                                  >
                                    Translation of the international application
                                  </button>
                                )}
                              <button
                                type="button"
                                onClick={() => {
                                  setActiveAmendmentUpload("unconvertedArchive")
                                  setAmendmentMenuOpen(false)
                                }}
                                className="block w-full bg-dantzLightBlue px-5 py-3 text-left hover:bg-slate-600"
                              >
                                Archive for unconverted files (ZIP)
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="min-h-28 space-y-4 px-6 py-6">
                      {activeAmendmentUpload === "priorityTranslation" || epPhase.priorityTranslationDocument ? (
                        <FileUploadSlot
                          label="Translation of the priority application(s)"
                          fileName={epPhase.priorityTranslationDocument?.fileName}
                          documentId={epPhase.priorityTranslationDocument?.epoDocumentId}
                          uploading={uploadingEpPhaseDocument === "priorityTranslation"}
                          error={epPhaseUploadErrors.priorityTranslation || ""}
                          disabled={isLocked}
                          onSelect={(file) =>
                            uploadEpPhaseFile(
                              file,
                              "PRIORITY_TRANSLATION",
                              "priorityTranslation",
                              (uploaded) => setEpPhaseField("priorityTranslationDocument", uploaded)
                            )
                          }
                          onDelete={() => setEpPhaseField("priorityTranslationDocument", null)}
                        />
                      ) : null}

                      {TRANSLATION_UPLOADS.map((upload) =>
                        activeAmendmentUpload === upload.key || epPhase[upload.field] ? (
                          <FileUploadSlot
                            key={upload.key}
                            label={upload.label}
                            fileName={epPhase[upload.field]?.fileName}
                            documentId={epPhase[upload.field]?.epoDocumentId}
                            uploading={uploadingEpPhaseDocument === upload.key}
                            error={epPhaseUploadErrors[upload.key] || ""}
                            disabled={isLocked}
                            onSelect={(file) =>
                              uploadEpPhaseFile(file, upload.documentType, upload.key, (uploaded) =>
                                setEpPhaseField(upload.field, uploaded)
                              )
                            }
                            onDelete={() => setEpPhaseField(upload.field, null)}
                          />
                        ) : null
                      )}

                      {AMENDMENT_UPLOADS.map((upload) =>
                        activeAmendmentUpload === upload.key || epPhase[upload.field] ? (
                          <FileUploadSlot
                            key={upload.key}
                            label={`${upload.label} (PDF, from page 1)`}
                            fileName={epPhase[upload.field]?.fileName}
                            documentId={epPhase[upload.field]?.epoDocumentId}
                            uploading={uploadingEpPhaseDocument === upload.key}
                            error={epPhaseUploadErrors[upload.key] || ""}
                            disabled={isLocked}
                            onSelect={(file) =>
                              uploadEpPhaseFile(file, upload.documentType, upload.key, (uploaded) =>
                                setEpPhaseField(upload.field, uploaded)
                              )
                            }
                            onDelete={() => setEpPhaseField(upload.field, null)}
                          />
                        ) : null
                      )}

                      {activeAmendmentUpload === "internationalTranslation" ||
                      epPhase.internationalApplicationTranslationDocument ? (
                        <FileUploadSlot
                          label="Translation of the international application (description, claims, abstract and drawings in one PDF)"
                          fileName={epPhase.internationalApplicationTranslationDocument?.fileName}
                          documentId={epPhase.internationalApplicationTranslationDocument?.epoDocumentId}
                          uploading={uploadingEpPhaseDocument === "internationalTranslation"}
                          error={epPhaseUploadErrors.internationalTranslation || ""}
                          disabled={isLocked}
                          onSelect={(file) =>
                            uploadEpPhaseFile(
                              file,
                              "INTERNATIONAL_TRANSLATION",
                              "internationalTranslation",
                              (uploaded) =>
                                setEpPhaseField("internationalApplicationTranslationDocument", uploaded)
                            )
                          }
                          onDelete={() =>
                            setEpPhaseField("internationalApplicationTranslationDocument", null)
                          }
                        />
                      ) : null}

                      {activeAmendmentUpload === "unconvertedArchive" || epPhase.unconvertedArchiveDocument ? (
                        <FileUploadSlot
                          label="Archive for unconverted files (ZIP)"
                          fileName={epPhase.unconvertedArchiveDocument?.fileName}
                          documentId={epPhase.unconvertedArchiveDocument?.epoDocumentId}
                          uploading={uploadingEpPhaseDocument === "unconvertedArchive"}
                          error={epPhaseUploadErrors.unconvertedArchive || ""}
                          disabled={isLocked}
                          accept="application/zip,.zip"
                          helperText="ZIP, max. 20 MB"
                          onSelect={(file) =>
                            uploadEpPhaseFile(
                              file,
                              "UNCONVERTED_ARCHIVE",
                              "unconvertedArchive",
                              (uploaded) => setEpPhaseField("unconvertedArchiveDocument", uploaded),
                              validatePatentZipFile
                            )
                          }
                          onDelete={() => setEpPhaseField("unconvertedArchiveDocument", null)}
                        />
                      ) : null}

                      {!activeAmendmentUpload &&
                        !epPhase.priorityTranslationDocument &&
                        !epPhase.internationalApplicationTranslationDocument &&
                        !epPhase.iperAnnexTranslationDocument &&
                        !epPhase.amendedClaimsTranslationDocument &&
                        !epPhase.amendedDescriptionDocument &&
                        !epPhase.amendedClaimsDocument &&
                        !epPhase.amendedDrawingsDocument &&
                        !epPhase.unconvertedArchiveDocument && (
                          <p className="py-7 text-sm font-semibold italic text-gray-400">
                            No amendment(s)/translation(s) found
                          </p>
                        )}
                    </div>
                  </div>
                </section>

                <section className="border-b border-gray-200 pb-8">
                  <h2 className="font-heading text-base font-semibold text-dantzBlue">
                    Information on documents and page numbers
                  </h2>

                  <div className="mt-5 bg-gray-50 px-5 py-4 text-sm text-dantzBlue">
                    <div className="mb-4 font-medium">
                      Basis for calculating the additional fee (Article 2(1), item 1a RFees)
                    </div>
                    <div className="max-w-sm space-y-2">
                      <div className="flex justify-between"><span>Number of pages - description</span><span>{descriptionPageTotal}</span></div>
                      <div className="flex justify-between"><span>Number of pages - claims</span><span>+ {claimsPageTotal}</span></div>
                      <div className="flex justify-between"><span>Number of pages - drawings</span><span>+ {drawingsPageTotal}</span></div>
                      <div className="flex justify-between"><span>Flat-rate pages - summary</span><span>+ 1</span></div>
                      <div className="flex justify-between border-t border-gray-300 pt-2 font-medium"><span>Total number of pages - filing</span><span>{totalFilingPages}</span></div>
                    </div>
                  </div>

                  <div className="bg-gray-50 px-5 py-5 text-sm text-dantzBlue">
                    <div className="mb-5 grid grid-cols-[1fr_16rem_12rem] gap-8 font-medium">
                      <div>Description - overview</div>
                      <div className="text-center">Page range (from to)</div>
                      <div className="text-center">Number of pages*</div>
                    </div>
                    <div className="grid grid-cols-[1fr_16rem_12rem] gap-8">
                      <div className="space-y-5">
                        <CheckboxRow label="Description of the PCT application as published" checked={epPhase.descriptionPctPublished} disabled={isLocked} onChange={(checked) => setEpPhaseField("descriptionPctPublished", checked)} />
                        <CheckboxRow label="Translation of the published description" checked={epPhase.descriptionTranslation} disabled={isLocked} onChange={(checked) => setEpPhaseField("descriptionTranslation", checked)} />
                        <CheckboxRow label="Amendment of the description under Article 34 PCT" checked={epPhase.descriptionArticle34Amendment} disabled={isLocked} onChange={(checked) => setEpPhaseField("descriptionArticle34Amendment", checked)} />
                        <CheckboxRow label="Translation of the amendment of the description under Article 34 PCT" checked={epPhase.descriptionArticle34Translation} disabled={isLocked} onChange={(checked) => setEpPhaseField("descriptionArticle34Translation", checked)} />
                        <CheckboxRow label="Amendments on entry into the European phase" checked={epPhase.descriptionEpPhaseAmendments} disabled={isLocked} onChange={(checked) => setEpPhaseField("descriptionEpPhaseAmendments", checked)} />
                      </div>
                      <div className="space-y-5">
                        <PageRangeInputSlot visible={epPhase.descriptionPctPublished} value={epPhase.descriptionPctPublishedPageRange} disabled={isLocked} hasError={!descriptionPctPublishedPagesValid} onChange={(value) => setPageRangeAndCount("descriptionPctPublishedPageRange", "descriptionPctPublishedPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.descriptionTranslation} value={epPhase.descriptionTranslationPageRange} disabled={isLocked} hasError={!descriptionTranslationPagesValid} onChange={(value) => setPageRangeAndCount("descriptionTranslationPageRange", "descriptionTranslationPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.descriptionArticle34Amendment} value={epPhase.descriptionArticle34AmendmentPageRange} disabled={isLocked} hasError={!descriptionArticle34AmendmentPagesValid} onChange={(value) => setPageRangeAndCount("descriptionArticle34AmendmentPageRange", "descriptionArticle34AmendmentPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.descriptionArticle34Translation} value={epPhase.descriptionArticle34TranslationPageRange} disabled={isLocked} hasError={!descriptionArticle34TranslationPagesValid} onChange={(value) => setPageRangeAndCount("descriptionArticle34TranslationPageRange", "descriptionArticle34TranslationPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.descriptionEpPhaseAmendments} value={epPhase.descriptionEpPhaseAmendmentsPageRange} disabled={isLocked} hasError={!descriptionEpPhaseAmendmentsPagesValid} onChange={(value) => setPageRangeAndCount("descriptionEpPhaseAmendmentsPageRange", "descriptionEpPhaseAmendmentsPageCount", value)} />
                      </div>
                      <div className="space-y-5">
                        <PageNumberInputSlot visible={epPhase.descriptionPctPublished} value={epPhase.descriptionPctPublishedPageCount} disabled={isLocked} hasError={!descriptionPctPublishedPagesValid} onChange={(value) => setEpPhaseField("descriptionPctPublishedPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.descriptionTranslation} value={epPhase.descriptionTranslationPageCount} disabled={isLocked} hasError={!descriptionTranslationPagesValid} onChange={(value) => setEpPhaseField("descriptionTranslationPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.descriptionArticle34Amendment} value={epPhase.descriptionArticle34AmendmentPageCount} disabled={isLocked} hasError={!descriptionArticle34AmendmentPagesValid} onChange={(value) => setEpPhaseField("descriptionArticle34AmendmentPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.descriptionArticle34Translation} value={epPhase.descriptionArticle34TranslationPageCount} disabled={isLocked} hasError={!descriptionArticle34TranslationPagesValid} onChange={(value) => setEpPhaseField("descriptionArticle34TranslationPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.descriptionEpPhaseAmendments} value={epPhase.descriptionEpPhaseAmendmentsPageCount} disabled={isLocked} hasError={!descriptionEpPhaseAmendmentsPagesValid} onChange={(value) => setEpPhaseField("descriptionEpPhaseAmendmentsPageCount", value)} />
                        <div className="border-t border-gray-300 pt-3">
                          <div className="font-medium">Number of pages - description</div>
                          <div className="mt-3">{descriptionPageTotal}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 px-5 py-5 text-sm text-dantzBlue">
                    <div className="mb-4 font-medium">Claims - overview</div>
                    <div className="mb-6 space-y-4">
                      <div>
                        <label className="mb-1 block font-medium">Number of claims for the European phase<span className="text-red-500">*</span></label>
                        <input type="number" min={1} step={1} value={epPhase.claimsForEuropeanPhaseCount} disabled={isLocked} onChange={(event) => setEpPhaseField("claimsForEuropeanPhaseCount", event.target.value)} className={fieldClass(!parsePageCount(epPhase.claimsForEuropeanPhaseCount), isLocked)} />
                      </div>
                      <div>
                        <label className="mb-1 block font-medium">Number of claims for which fees are paid (including the first 15 claims)<span className="text-red-500">*</span></label>
                        <input type="number" min={1} step={1} value={epPhase.claimsFeePaidCount} disabled={isLocked} onChange={(event) => setEpPhaseField("claimsFeePaidCount", event.target.value)} className={fieldClass(!parsePageCount(epPhase.claimsFeePaidCount), isLocked)} />
                      </div>
                    </div>
                    <div className="mb-5 grid grid-cols-[1fr_16rem_12rem] gap-8 font-medium">
                      <div></div>
                      <div className="text-center">Page range (from to)</div>
                      <div className="text-center">Number of pages*</div>
                    </div>
                    <div className="grid grid-cols-[1fr_16rem_12rem] gap-8">
                      <div className="space-y-5">
                        <CheckboxRow label="Claims of the PCT application as published" checked={epPhase.claimsPctPublished} disabled={isLocked} onChange={(checked) => setEpPhaseField("claimsPctPublished", checked)} />
                        <CheckboxRow label="Claims amended under Article 19 PCT" checked={epPhase.claimsArticle19Amendment} disabled={isLocked} onChange={(checked) => setEpPhaseField("claimsArticle19Amendment", checked)} />
                        <CheckboxRow label="Claims amended under Article 34 PCT" checked={epPhase.claimsArticle34Amendment} disabled={isLocked} onChange={(checked) => setEpPhaseField("claimsArticle34Amendment", checked)} />
                        <CheckboxRow label="Amendments on entry into the European phase" checked={epPhase.claimsEpPhaseAmendments} disabled={isLocked} onChange={(checked) => setEpPhaseField("claimsEpPhaseAmendments", checked)} />
                      </div>
                      <div className="space-y-5">
                        <PageRangeInputSlot visible={epPhase.claimsPctPublished} value={epPhase.claimsPctPublishedPageRange} disabled={isLocked} hasError={!claimsPctPublishedPagesValid} onChange={(value) => setPageRangeAndCount("claimsPctPublishedPageRange", "claimsPctPublishedPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.claimsArticle19Amendment} value={epPhase.claimsArticle19AmendmentPageRange} disabled={isLocked} hasError={!claimsArticle19AmendmentPagesValid} onChange={(value) => setPageRangeAndCount("claimsArticle19AmendmentPageRange", "claimsArticle19AmendmentPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.claimsArticle34Amendment} value={epPhase.claimsArticle34AmendmentPageRange} disabled={isLocked} hasError={!claimsArticle34AmendmentPagesValid} onChange={(value) => setPageRangeAndCount("claimsArticle34AmendmentPageRange", "claimsArticle34AmendmentPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.claimsEpPhaseAmendments} value={epPhase.claimsEpPhaseAmendmentsPageRange} disabled={isLocked} hasError={!claimsEpPhaseAmendmentsPagesValid} onChange={(value) => setPageRangeAndCount("claimsEpPhaseAmendmentsPageRange", "claimsEpPhaseAmendmentsPageCount", value)} />
                      </div>
                      <div className="space-y-5">
                        <PageNumberInputSlot visible={epPhase.claimsPctPublished} value={epPhase.claimsPctPublishedPageCount} disabled={isLocked} hasError={!claimsPctPublishedPagesValid} onChange={(value) => setEpPhaseField("claimsPctPublishedPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.claimsArticle19Amendment} value={epPhase.claimsArticle19AmendmentPageCount} disabled={isLocked} hasError={!claimsArticle19AmendmentPagesValid} onChange={(value) => setEpPhaseField("claimsArticle19AmendmentPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.claimsArticle34Amendment} value={epPhase.claimsArticle34AmendmentPageCount} disabled={isLocked} hasError={!claimsArticle34AmendmentPagesValid} onChange={(value) => setEpPhaseField("claimsArticle34AmendmentPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.claimsEpPhaseAmendments} value={epPhase.claimsEpPhaseAmendmentsPageCount} disabled={isLocked} hasError={!claimsEpPhaseAmendmentsPagesValid} onChange={(value) => setEpPhaseField("claimsEpPhaseAmendmentsPageCount", value)} />
                        <div className="border-t border-gray-300 pt-3">
                          <div className="font-medium">Number of pages - claims</div>
                          <div className="mt-3">{claimsPageTotal}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 px-5 py-5 text-sm text-dantzBlue">
                    <div className="mb-5 grid grid-cols-[1fr_16rem_12rem] gap-8 font-medium">
                      <div>Drawings - overview</div>
                      <div className="text-center">Page range (from to)</div>
                      <div className="text-center">Number of pages*</div>
                    </div>
                    <div className="grid grid-cols-[1fr_16rem_12rem] gap-8">
                      <div className="space-y-5">
                        <CheckboxRow label="Drawings of the PCT application as published" checked={epPhase.drawingsPctPublished} disabled={isLocked} onChange={(checked) => setEpPhaseField("drawingsPctPublished", checked)} />
                        <CheckboxRow label="Translation of the published drawings" checked={epPhase.drawingsTranslation} disabled={isLocked} onChange={(checked) => setEpPhaseField("drawingsTranslation", checked)} />
                        <CheckboxRow label="Drawings amended under Article 34 PCT" checked={epPhase.drawingsArticle34Amendment} disabled={isLocked} onChange={(checked) => setEpPhaseField("drawingsArticle34Amendment", checked)} />
                        <CheckboxRow label="Amendments on entry into the European phase" checked={epPhase.drawingsEpPhaseAmendments} disabled={isLocked} onChange={(checked) => setEpPhaseField("drawingsEpPhaseAmendments", checked)} />
                      </div>
                      <div className="space-y-5">
                        <PageRangeInputSlot visible={epPhase.drawingsPctPublished} value={epPhase.drawingsPctPublishedPageRange} disabled={isLocked} hasError={!drawingsPctPublishedPagesValid} onChange={(value) => setPageRangeAndCount("drawingsPctPublishedPageRange", "drawingsPctPublishedPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.drawingsTranslation} value={epPhase.drawingsTranslationPageRange} disabled={isLocked} hasError={!drawingsTranslationPagesValid} onChange={(value) => setPageRangeAndCount("drawingsTranslationPageRange", "drawingsTranslationPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.drawingsArticle34Amendment} value={epPhase.drawingsArticle34AmendmentPageRange} disabled={isLocked} hasError={!drawingsArticle34AmendmentPagesValid} onChange={(value) => setPageRangeAndCount("drawingsArticle34AmendmentPageRange", "drawingsArticle34AmendmentPageCount", value)} />
                        <PageRangeInputSlot visible={epPhase.drawingsEpPhaseAmendments} value={epPhase.drawingsEpPhaseAmendmentsPageRange} disabled={isLocked} hasError={!drawingsEpPhaseAmendmentsPagesValid} onChange={(value) => setPageRangeAndCount("drawingsEpPhaseAmendmentsPageRange", "drawingsEpPhaseAmendmentsPageCount", value)} />
                      </div>
                      <div className="space-y-5">
                        <PageNumberInputSlot visible={epPhase.drawingsPctPublished} value={epPhase.drawingsPctPublishedPageCount} disabled={isLocked} hasError={!drawingsPctPublishedPagesValid} onChange={(value) => setEpPhaseField("drawingsPctPublishedPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.drawingsTranslation} value={epPhase.drawingsTranslationPageCount} disabled={isLocked} hasError={!drawingsTranslationPagesValid} onChange={(value) => setEpPhaseField("drawingsTranslationPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.drawingsArticle34Amendment} value={epPhase.drawingsArticle34AmendmentPageCount} disabled={isLocked} hasError={!drawingsArticle34AmendmentPagesValid} onChange={(value) => setEpPhaseField("drawingsArticle34AmendmentPageCount", value)} />
                        <PageNumberInputSlot visible={epPhase.drawingsEpPhaseAmendments} value={epPhase.drawingsEpPhaseAmendmentsPageCount} disabled={isLocked} hasError={!drawingsEpPhaseAmendmentsPagesValid} onChange={(value) => setEpPhaseField("drawingsEpPhaseAmendmentsPageCount", value)} />
                        <div className="border-t border-gray-300 pt-3">
                          <div className="font-medium">Number of pages - drawings</div>
                          <div className="mt-3">{drawingsPageTotal}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  <h2 className="font-heading text-base font-semibold text-dantzBlue">
                    Additional documents
                  </h2>

                  <CheckboxRow
                    label="Add further documents to the filing"
                    checked={epPhase.additionalDocumentsEnabled}
                    disabled={isLocked}
                    onChange={(checked) => {
                      setEpPhase((current) => ({
                        ...current,
                        additionalDocumentsEnabled: checked
                      }))
                      if (!checked) setSelectedAdditionalDocumentIds([])
                    }}
                  />

                  {epPhase.additionalDocumentsEnabled && (
                    <div className="space-y-3 bg-gray-50 px-4 py-4">
                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={removeSelectedAdditionalDocuments}
                          disabled={isLocked || selectedAdditionalDocumentIds.length === 0}
                          className="px-4 py-2 text-sm font-semibold text-gray-400 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                        <button
                          type="button"
                          onClick={() => setAdditionalDocumentModalOpen(true)}
                          disabled={isLocked}
                          className="bg-dantzBlue px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-dantzLightBlue disabled:opacity-60"
                        >
                          Add
                        </button>
                      </div>

                      <div className="overflow-x-auto bg-white">
                        <table className="w-full min-w-[42rem] text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-500">
                              <th className="w-12 px-3 py-3"></th>
                              <th className="px-3 py-3">Original file name</th>
                              <th className="px-3 py-3">Document type (or user-assigned name for Other)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {activeAdditionalDocuments.length === 0 ? (
                              <tr>
                                <td colSpan={3} className="px-3 py-5 text-sm text-gray-400">
                                  No additional documents added.
                                </td>
                              </tr>
                            ) : (
                              activeAdditionalDocuments.map((item) => (
                                <tr key={item.id} className="text-dantzBlue">
                                  <td className="px-3 py-2">
                                    <input
                                      type="checkbox"
                                      checked={selectedAdditionalDocumentIds.includes(item.id)}
                                      disabled={isLocked}
                                      onChange={(event) =>
                                        toggleAdditionalDocumentSelection(
                                          item.id,
                                          event.target.checked
                                        )
                                      }
                                      className="h-4 w-4 border-gray-300"
                                    />
                                  </td>
                                  <td className="px-3 py-2">{item.document?.epoDocumentId && item.document.fileName?.toLowerCase().endsWith(".pdf") ? <PdfPreview provider="patent" documentId={item.document.epoDocumentId} fileName={item.document.fileName} /> : item.document?.fileName || ""}</td>
                                  <td className="px-3 py-2">
                                    {item.documentKind === "Other documents" && item.customName
                                      ? item.customName
                                      : item.documentKind}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            )}

            {step === 4 && (
              <div className="flex-col gap-10 flex">
                <PatentEpPhaseFeesSection applicationId={draftId} disabled={isLocked} />
              </div>
            )}

            {step === FORM_STEPS.length - 1 && (
              <div className="flex-col gap-10 flex">
                <div id="signature">
                  <SignatureSection
                    onChange={setSignature}
                    initialData={signature}
                    hideCapacity
                    hideNotice
                    disabled={isLocked}
                  />
                </div>
              </div>
            )}

            {additionalDocumentModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                <div
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="additional-document-dialog-title"
                  className="w-full max-w-3xl bg-white p-6 shadow-xl"
                >
                  <div className="mb-5 flex items-center justify-between bg-gray-100 px-4 py-2">
                    <h3 id="additional-document-dialog-title" className="text-base font-semibold text-dantzBlue">Upload file</h3>
                    <button
                      type="button"
                      onClick={closeAdditionalDocumentModal}
                      className="text-2xl leading-none text-dantzBlue"
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>

                  <div className="space-y-5">
                    <FileUploadSlot
                      fileName={additionalDocumentFile?.fileName}
                      documentId={additionalDocumentFile?.epoDocumentId}
                      uploading={uploadingEpPhaseDocument === "additionalDocument"}
                      error={epPhaseUploadErrors.additionalDocument || ""}
                      disabled={isLocked}
                      onSelect={uploadAdditionalDocumentFile}
                      onDelete={() => setAdditionalDocumentFile(null)}
                    />

                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="mb-3 text-sm font-semibold text-dantzBlue">Document type</h4>
                      <p className="mb-4 text-sm text-dantzBlue">
                        Select the document type you want to upload:
                      </p>

                      <div className="grid grid-cols-[12rem_1fr] items-center gap-4 text-sm">
                        <label className="text-right font-medium text-dantzBlue">
                          Document type*
                        </label>
                        <select
                          value={additionalDocumentKind}
                          onChange={(event) => setAdditionalDocumentKind(event.target.value)}
                          disabled={isLocked}
                          className={fieldClass(false, isLocked)}
                        >
                          <option value="">- Select -</option>
                          {ADDITIONAL_DOCUMENT_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>

                        <label className="text-right font-medium text-dantzBlue">
                          Document name*
                        </label>
                        <input
                          type="text"
                          value={additionalDocumentName}
                          onChange={(event) => setAdditionalDocumentName(event.target.value)}
                          disabled={isLocked}
                          className={fieldClass(false, isLocked)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end gap-3 border-t border-gray-200 pt-4">
                    <button
                      type="button"
                      onClick={closeAdditionalDocumentModal}
                      className="bg-gray-300 px-5 py-2 text-sm font-semibold text-dantzBlue hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={addAdditionalDocument}
                      disabled={
                        isLocked ||
                        !additionalDocumentKind ||
                        !additionalDocumentName.trim() ||
                        !additionalDocumentFile ||
                        uploadingEpPhaseDocument === "additionalDocument"
                      }
                      className="bg-dantzBlue px-5 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-dantzLightBlue disabled:opacity-60"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-10 flex items-center justify-between border-t border-gray-100 pt-6">
              <button
                type="button"
                hidden={step === 0}
                onClick={() => goToStep(step - 1)}
                disabled={savingDraft}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="button"
                hidden={step === FORM_STEPS.length - 1}
                onClick={() => goToStep(step + 1)}
                disabled={savingDraft}
                className="ml-auto rounded-lg bg-dantzBlue px-5 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          <aside className="hidden md:block">
            <div className="sticky top-[128px] flex max-h-[calc(100vh-9rem)] w-72 flex-col gap-4 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
                <div className="mb-1 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </span>
                  <StatusBadge status={applicationStatus} />
                </div>
                {!isLocked && (
                  <>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        savingDraft
                          ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                          : "border-gray-300 bg-white text-dantzBlue hover:border-dantzBlue hover:bg-blue-50"
                      }`}
                    >
                      <FiSave className="h-4 w-4" aria-hidden="true" />
                      {savingDraft ? <SpinnerText>Saving draft...</SpinnerText> : "Save draft"}
                    </button>

                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        savingDraft
                          ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
                          : "border-gray-300 bg-white text-dantzBlue hover:border-dantzBlue hover:bg-blue-50"
                      }`}
                    >
                      <FiRefreshCw className="h-4 w-4" aria-hidden="true" />
                      Reset form
                    </button>

                    <button
                      type="button"
                      onClick={handleProceedToPayment}
                      disabled={!paymentEnabled || savingDraft}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        paymentEnabled && !savingDraft
                          ? "bg-dantzOrange text-white hover:brightness-110 active:scale-[0.98]"
                          : "cursor-not-allowed bg-gray-300 text-gray-500"
                      }`}
                    >
                      <FiCreditCard className="h-4 w-4" aria-hidden="true" />
                      Proceed to payment
                    </button>
                  </>
                )}
              </div>
              <FilingSidebarAccordion>
                <PatentApplicationFeesBox
                  applicationId={draftId}
                  procedureType="EP1200"
                  refreshToken={feeRefreshToken}
                />
                <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
                  <CurrencySelector />
                </div>
                <ServiceDescriptions type="patent-pct" />
              </FilingSidebarAccordion>
            </div>
          </aside>
        </div>
      </div>
    </AppLayout>
  )
}
